import { supabase } from './supabaseClient';
import { journalMirrorOutboxStore, JournalMirrorOutboxEntry } from './journalMirrorOutbox';
import { getQueueEntry } from './offlineQueue';
import { LinkedBillingMode, billingModeAt } from './linkedBillingMode';
import { LinkedProjectConfig, ServiceTransaction, type ServiceJournalKind } from '../types';
// Blattmodul OHNE Laufzeit-Importe (serviceLogUtils.ts, siehe asDay) — der
// Import kann deshalb keinen Zyklus bilden.
import { berlinerZeitstempel, buchungstag, isFlatProofEntry, istProjektRegistriert, registriereBuchungsarten, vergissBuchungsartenFuer } from './serviceLogUtils';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DAS BUCHUNGSJOURNAL — Abbildung, Zugriff, Anhaengen
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Das Leistungsprotokoll steht bis heute AUSSCHLIESSLICH als Array im
 * Projekt-JSON (`projects.data -> metadata -> globalData -> serviceLog`).
 * Rund 65 Stellen packen es dort selbst aus; jede von ihnen entscheidet
 * eigenstaendig, was eine Buchung ist, was eine erbrachte Leistung und
 * welchem Kalendertag eine Zeile gehoert. Genau daraus stammt die Mehrzahl
 * der dokumentierten Geldvorfaelle.
 *
 * `public.service_journal` (SQL Nr. 59) ist die relationale Zweitschrift
 * desselben Protokolls: eine Zeile je Eintrag, mit den Aussagen als SPALTEN
 * statt als Text, den jede Stelle neu auslegt.
 *
 * ─── Was dieses Modul ist, und was es NICHT ist ────────────────────────────
 * Dieses Modul ist Schritt S4a: die Abbildung und der Zugriff. Es hat noch
 * KEINE Aufrufstelle. Kein bestehender Schreibpfad ist geaendert, keine
 * Anzeige liest von hier. Das Verdrahten ist S4b.
 *
 * ─── Die Abbildung hat zwei Implementierungen ──────────────────────────────
 * Dieselbe Abbildung entsteht ein zweites Mal in SQL (Migration Nr. 60, die
 * Bestandsuebernahme). Weicht eine der beiden ab, meldet die spaetere
 * Abweichungssicht `v_service_journal_drift` dauerhaft Unterschiede, die
 * keine sind — und eine Sicht, der niemand mehr traut, ist schlimmer als
 * keine. Verbindlich ist die Spezifikation ABBILDUNG.md; wer hier etwas
 * aendert, aendert Nr. 60 mit.
 *
 * ─── Der Client wird VOR dem SQL ausgeliefert ──────────────────────────────
 * Auf einer Umgebung ohne die Migration muss sich die App byte-gleich wie
 * heute verhalten. Deshalb schluckt `appendToJournal` eine fehlende RPC
 * (PGRST202) und `loadJournal` eine fehlende Tabelle, und `getServiceLog`
 * faellt in beiden Faellen auf das JSON zurueck — dasselbe Muster wie
 * `cloudService.syncChildrenTransactional` und `projectLockService`.
 *
 * ─── BEKANNTE ABWEICHUNGEN der Doppelschreibphase ──────────────────────────
 * Die Abweichungssicht `v_service_journal_drift` meldet drei Klassen, die
 * ERWARTET sind und keine Fehler. Sie stehen hier, weil eine Sicht, deren
 * Meldungen niemand mehr einordnen kann, schlimmer ist als keine Sicht.
 *
 * 1. ZURUECKGENOMMENE ZEILEN — das Journal hat eine zu viel. Wo ein Pfad eine
 *    JSON-Zeile ENTFERNT, bleibt die gespiegelte Zeile stehen: das Journal
 *    kennt kein Delete. Diese Klasse ist inzwischen GESCHLOSSEN, auf zwei
 *    verschiedenen Wegen, und der Unterschied ist der Punkt:
 *
 *    - Liegen Schreiben und Ruecknahme im SELBEN Vorgang, wandert das
 *      Anhaengen hinter die letzte Stelle, an der noch kompensiert werden
 *      kann — dann gibt es nichts zurueckzunehmen. So beim Ausstellen einer
 *      Rechnung (`issueInvoice`/`revertWriteBack`).
 *    - Nimmt ein SPAETERER Vorgang zurueck, was ein frueherer richtig
 *      gespiegelt hat, hilft kein Aufschub: die Zeile steht laengst. Diese
 *      Pfade rufen `voidJournalEntries` (`handleRestorePosition`,
 *      `revertBackfill` in beiden Richtungen, `planReversal`).
 *
 *    Was bleibt: bricht die App zwischen dem JSON-Schreiben und der
 *    Ruecknahme ab, steht die Zeile weiter im Journal — sichtbar in der
 *    Abweichungssicht und mit einem erneuten Aufruf nachholbar.
 *
 *    Und eine GRENZE, die an einer einzigen Zeile fremden Codes haengt: wird
 *    eine Zeile zurueckgenommen und danach eine INHALTSGLEICHE neu
 *    geschrieben, taeten beide denselben Schluessel tragen, sobald die neue
 *    ohne brauchbare id ins JSON kaeme — `on conflict do nothing` schriebe
 *    dann nichts, und die zurueckgenommene Zeile bliebe zurueckgenommen,
 *    obwohl das JSON sie wieder fuehrt. Un-void gibt es nicht, und in der
 *    umgekehrten Reihenfolge endet es genauso; heilbar waere es nur mit einem
 *    Update, das die Tabelle bewusst nicht hat. Erreichbar ist der Fall heute
 *    NICHT, weil jeder Erzeuger (`buildBackfillServices`, die Rechnungs-
 *    Rueckschreibung, die Ruecknahmespur) jeder Zeile eine frische uuid gibt.
 *    Wer je eine Protokollzeile ohne id schreibt, macht ihn erreichbar.
 * 2. GEAENDERTE ZEILEN — Journal und JSON tragen dieselbe id mit anderem
 *    Inhalt. `moveAltBackfill` schreibt `serviceId`/`quantity` bestehender
 *    ALT-Zeilen um, das LEGACY-AUTO-Stempeln setzt `bookedAt`/
 *    `bookingReference` nachtraeglich, `assignUnassignedBilled` stempelt
 *    `customerId`/`splitPartyId`. Alles Update, kein Append — S4b spiegelt
 *    nur NEUE Zeilen.
 * 3. VERLORENE VORMERKUNGEN — das JSON hat eine zu viel. Die Pfade, die nur
 *    `onUpdate` rufen, merken ihre Zweitschrift vor und schreiben sie erst
 *    nach dem bestaetigten Flush (`queueJournalMirror`). Bis zum 07.09.2026
 *    lebte die Vormerkung nur im Speicher, waehrend die Offline-Queue den
 *    Stand spaeter aus IndexedDB flushte; dazu scheiterte das direkte
 *    Anhaengen nach dem Versand still (ohne `await`, Seite geschlossen).
 *    Seitdem liegt beides im Ausgangskorb (`journalMirrorOutbox.ts`) und
 *    `retryJournalMirrors` holt es nach — nach Ruecksprache mit dem Server,
 *    ob das JSON die Zeile traegt. Was trotzdem durchrutscht, holt die
 *    Nachspiegelung Nr. 76 (`service_journal_nachspiegeln`) jede Nacht.
 *
 * Alle drei loest dieselbe Richtung auf: die Reparatur JSON -> Journal muss
 * WIEDERHOLBAR sein und den ganzen Bestand pruefen, nicht nur den Zuwachs.
 * Die umgekehrte Richtung gibt es nicht, solange das JSON fuehrt.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Typen
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die Buchungsart einer Zeile — die Aussage, die heute an rund fuenfzehn
 * Stellen aus Praefixen und Textmustern neu erschlossen wird.
 *
 * `is_posting` (Geldvorgang statt Arbeit) ist in der Tabelle eine GENERIERTE
 * Spalte und wird hier bewusst nicht abgebildet: zwei Wahrheiten nebeneinander
 * liefen frueher oder spaeter auseinander.
 */
export type JournalKind = ServiceJournalKind;

/**
 * Woher der Berichtsbezug stammt — und wie belastbar er ist.
 *
 * `text_exact` (seit Nr. 119): kein zerlegbarer Ref, aber die kanonische
 * Hauptbuchung „Bericht #N abgeschlossen" — die Nummer ist dort so sicher wie
 * im Ref, nur der Abschnitt fehlt. Nicht zu verwechseln mit `heuristic`, den
 * Alt-Schreibweisen, die nie eine Nummer bekommen.
 */
export type JournalParseConfidence = 'exact' | 'text_exact' | 'heuristic' | 'none';

/**
 * Der Schreibpfad, aus dem die Zeile stammt.
 *
 * `'migration_json'` gehoert der Bestandsuebernahme (Nr. 60) und darf vom
 * Client nie geschrieben werden — sonst laesst sich hinterher nicht mehr
 * trennen, was kopiert und was neu gebucht wurde.
 */
export type JournalSource =
    | 'migration_json'
    | 'report_send'
    | 'child_to_parent'
    // Der Umzug der Positionen einer Tochter an die Mutter beim VERKNUEPFEN
    // (cloudService.applyLinkTransfer). Nicht dasselbe wie 'child_to_parent':
    // dort bucht eine bestehende Tochter auf das LV der Mutter, hier wechselt
    // die Zeile einmalig das Projekt. Die Spalte `source` traegt keinen CHECK
    // (Nr. 59) — ein neuer Wert braucht deshalb kein SQL.
    | 'link_transfer'
    // Abrechnung von Unterweisungs-Teilnahmen (SiFa-Modul, docs/sifa-unterweisung-plan.md §7).
    // Der DB-Riegel `sifa_teilnahme_gebucht_markieren` laeuft VOR dieser Zeile.
    | 'unterweisung'
    // Zeitaufwand einer Gefaehrdungsbeurteilung (SiFa-Modul, docs/sifa-gbu-plan.md §10).
    // Der DB-Riegel `sifa_taetigkeit_gebucht_markieren` laeuft VOR dieser Zeile.
    | 'gbu'
    | 'invoice_writeback'
    | 'admin_accounting'
    | 'manual_billing'
    | 'waiver'
    | 'reversal'
    | 'flat_rate'
    | 'flat_rate_switch'
    | 'rebook'
    | 'undated_backlog'
    | 'performance_proof'
    | 'alt_import'
    | 'einweisung'
    // Paketposition mit Bestandteilen (docs/positionsbestandteile-plan.md):
    // die automatische Positionsbuchung `BST-<serviceId>` und ihre RN-Spur,
    // geschrieben aus bestandteile/bestandteilBuchung.ts (Muster B).
    | 'bestandteile'
    // Buchung aus dem Baustellenordnungs-Modul (AusgabeReiter/bucheBauordnung)
    // und aus dem A+S-Plan — bisher ohne eigenen Wert (Plan B5). Wie bei
    // 'link_transfer': `source` hat keinen CHECK (Nr. 59), kein SQL noetig.
    | 'bauordnung'
    | 'asplan'
    | 'sql_fix'
    // Nr. 76 `service_journal_nachspiegeln`: Zeilen, die nur im JSON standen
    // und vom Nachtlauf ins Journal geholt wurden. Nie vom Client geschrieben.
    | 'json_repair';

/**
 * Eine Zeile von `public.service_journal`.
 *
 * Bewusst in snake_case: das Objekt geht unveraendert als jsonb-Element an
 * `service_journal_append(p_project_id, p_entries)` und kommt unveraendert aus
 * dem SELECT zurueck. Eine Umbenennung auf dem Weg waere eine dritte Stelle,
 * an der die Abbildung auseinanderlaufen kann.
 */
export interface ServiceJournalRow {
    id: string;
    project_id: string;
    service_id: string;
    quantity: number;
    entry_at: string;
    /** ORTSZEIT-Kalendertag von `entry_at`, `YYYY-MM-DD`. Siehe `entryDay`. */
    entry_day: string | null;
    kind: JournalKind;
    report_ref: string | null;
    report_number: number | null;
    report_section_id: string | null;
    report_child_project_id: string | null;
    parse_confidence: JournalParseConfidence;
    is_addon: boolean;
    covers_inspection: boolean;
    forced: boolean;
    booking_reference: string | null;
    /** ANZEIGE ONLY — nie Grundlage einer Entscheidung, dafuer gibt es `kind`. */
    description: string | null;
    performer_id: string | null;
    performer_name: string | null;
    is_substitution: boolean;
    booked_at: string | null;
    booked_by: string | null;
    booked_by_id: string | null;
    customer_id: string | null;
    /** Teilnehmer des Aufteilungsschluessels (Abschnitte, SQL Nr. 54). */
    split_party_id: string | null;
    /** Anteil in Basispunkten, mit dem gebucht wurde (5000 = 50,00 %). */
    split_share_bp: number | null;
    applied_used: number | null;
    applied_billed: number | null;
    billing_mode_at_entry: LinkedBillingMode | null;
    source: JournalSource;
    /** Die urspruengliche, nicht als uuid brauchbare `id` des JSON-Eintrags. */
    legacy_entry_id: string | null;
    /**
     * ─── Die drei Felder der Ruecknahmespur (SQL Nr. 116/117) ──────────────
     *
     * Nur auf einer Spur ('RN-…', `kind = 'reversal'`) belegt, sonst `null`:
     * WELCHER Eintrag zurueckgenommen wurde, zu welchem Bericht er gehoerte,
     * und um welche Menge ein stehender Eintrag gekuerzt wurde (teilweise
     * Ruecknahme). `reversed_entry_id` ist `text` und nicht uuid — die Kennung
     * ist die aus dem serviceLog, und die ist oft keine.
     *
     * Sie muessen mit, weil `annotateReversibleEntries`
     * (serviceReversalLogic.ts) `partiallyReversed` und `priorReversals` ALLEIN
     * aus ihnen bildet. Sobald `getServiceLog` das Protokoll aus dem Journal
     * liest (S5c), waeren ohne sie beide Aussagen des Ruecknahmedialogs weg.
     *
     * OPTIONAL wie `voided_at`, und aus demselben Grund: eine Zeile, die VOR
     * SQL Nr. 116 gelesen wurde, hat die Spalten gar nicht. Der Typ soll das
     * sagen koennen, ohne dass eine Lesestelle sie wegcasten muss.
     */
    reversed_entry_id?: string | null;
    reversed_report_ref?: string | null;
    reversed_quantity?: number | null;
    /** Nur beim LESEN belegt (Ruecknahme ueber `service_journal_void`). */
    voided_at?: string | null;
}

/**
 * Was die AUFRUFSTELLE ueber eine Zeile weiss und der Text nicht hergibt.
 *
 * `book()` in reportBooking.ts weiss, ob es gerade eine Hauptbuchung
 * (`mainBookings`) oder eine Zusatzleistung (`additionalAddons`) schreibt.
 * Genau das ist der Sinn dieser beiden Spalten: der `reportRef` steht auf
 * BEIDEN und beweist deshalb keine Begehung — die dokumentierte Klasse
 * „gebucht wurde nur die Zusatzleistung, die Begehung selbst nicht"
 * (21 Eintraege auf 14 Sichtzeilen, Riegel 3 in unbooked_reports_legacy.sql).
 *
 * Fehlt die Aussage, wird sie aus dem Text erschlossen — dieselbe Regel, die
 * die Migration anwendet (`inferInspectionCoverage`). Der Weg unterscheidet
 * sich, das ERGEBNIS muss dasselbe sein.
 */
export interface JournalEntryStatement {
    isAddon?: boolean;
    coversInspection?: boolean;
}

/** Eingang von `toJournalRows`. */
export interface JournalMappingInput {
    projectId: string;
    /**
     * Die Eintraege in der Reihenfolge, in der sie im JSON stehen. Die
     * Reihenfolge ist bedeutungstragend: sie geht als `array_index` in die
     * deterministische Ersatz-id ein.
     */
    entries: ServiceTransaction[];
    source: JournalSource;
    /**
     * Fuer `billing_mode_at_entry`. Fehlt sie, bleibt die Spalte `null` — der
     * Leser faellt dann auf `billingModeAt` zurueck, also in Richtung
     * „sperrt", die sichere Seite.
     */
    linkedProjectConfig?: LinkedProjectConfig | null;
    /** Aussage der Aufrufstelle, je `ServiceTransaction.id`. */
    statements?: Record<string, JournalEntryStatement>;
    /**
     * Bereits im Projekt vergebene Eintrags-ids. Eine erneut auftauchende id
     * wird neu geschluesselt; die ERSTE behaelt ihre.
     *
     * Es gibt bewusst KEIN `startIndex` mehr: die Ersatz-id haengt seit der
     * Korrektur der Spezifikation am INHALT und nicht mehr an der
     * Arrayposition (Begruendung an `deterministicEntryId`).
     */
    knownIds?: Iterable<string>;
}

/** Warum eine Zeile ihre id nicht behalten konnte (`service_journal_unkeyed`). */
export type JournalUnkeyedReason =
    | 'keine_id'
    | 'keine_uuid'
    | 'doppelt_im_projekt'
    | 'doppelt_global';

/** Ergebnis der Schluesselvergabe fuer genau einen Eintrag. */
export interface JournalIdDecision {
    id: string;
    legacyEntryId: string | null;
    reason: JournalUnkeyedReason | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Bausteine der Abbildung
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `entry_day` — der Kalendertag in `Europe/Berlin`.
 *
 * ─── Warum eine FESTE Zone und nicht die des Geraets ───────────────────────
 * Der erste Entwurf hat `archivTag` (reportRebookLogic.ts) uebernommen, und
 * das rechnet in der Zeitzone des Geraets. Im Inland ist das dasselbe; bei
 * einem Anwender ausserhalb Deutschlands liefe der Client gegen den Server.
 * Das Journal muss serverseitig reproduzierbar sein — die Abweichungssicht
 * vergleicht beide Fassungen —, also gewinnt die feste Zone. Die
 * Spezifikation legt `Europe/Berlin` fuer BEIDE Implementierungen fest.
 *
 * ─── Warum ueberhaupt umgerechnet wird ─────────────────────────────────────
 * Der Textschnitt (`asDay` in serviceLogUtils.ts) beantwortet eine ANDERE
 * Frage: dort geht es darum, einen vollen Zeitstempel mit einem als reinen Tag
 * geschriebenen ZEITRAUMENDE vergleichbar zu machen. Hier geht es um den Tag,
 * an dem die Begehung STATTFAND. Lokale Mitternacht des 05.05. ist in MEZ
 * `2026-05-04T22:00:00Z` — der Textschnitt ergaebe den 04.05., und der Bericht
 * wuerde einen Tag zu frueh gebucht. Auf einem Tagesriegel („zwei Begehungen
 * an einem Tag") ist das unmittelbar eine falsche Geldforderung.
 *
 * ─── Der Zeitstempel OHNE Zonenangabe ──────────────────────────────────────
 * `new Date('2026-05-05T08:30:00')` liest JavaScript als GERAETEZEIT — genau
 * die Abhaengigkeit, die hier verschwinden soll. Ein solcher Wert ist die
 * Wanduhrzeit des Betriebs, also bereits Berliner Zeit; sein Kalendertag steht
 * damit unveraendert in der Zeichenkette und wird nicht durch ein Date-Objekt
 * geschickt. Postgres verfaehrt mit `timestamp without time zone` genauso.
 *
 * S5d: die Rechnung selbst steht als `buchungstag` im Blattmodul
 * serviceLogUtils.ts — dieselbe Funktion rechnen seitdem auch alle
 * Tagesriegel (`book()`, Nachbuchen, Tagespauschale im Versandpfad und im
 * Nachtlauf). Spalte und Riegel koennen damit nicht auseinanderlaufen.
 */
export const entryDay = (value?: string | null): string | null => buchungstag(value);

/** Wie `flatRateLogic.FLAT_KEY_SHAPE` — ein Monatsschluessel `FLAT-YYYY-MM`. */
const FLAT_KEY_SHAPE = /^FLAT-\d{4}-(0[1-9]|1[0-2])$/;

/** Wie `serviceLogUtils.isManualPosting` — erkannt am TEXT, nie am Praefix. */
const MANUELLE_VERBUCHUNG = /manuelle\s+verbuchung/i;

/**
 * `kind` — die Buchungsart. Die Reihenfolge der Pruefung ist BINDEND, die
 * erste Uebereinstimmung gewinnt (ABBILDUNG.md).
 *
 * ─── Der haeufigste Fehler steckt in den Regeln, die hier NICHT stehen ─────
 * `LEGACY-AUTO` und `MANUAL-` sind KEINE Buchungen. Sie stehen laut
 * serviceLogUtils.ts ausdruecklich auf echten, von Hand nachgetragenen
 * Leistungen und muessen abrechenbar bleiben — deshalb faellt beides durch bis
 * auf `work`. `MANUAL-` wird nur dann zur Buchung, wenn der TEXT es sagt
 * (Regel 7), genau wie `isManualPosting` seit jeher unterscheidet: das Praefix
 * taugt dafuer nicht.
 *
 * `ALT-` bekommt eine eigene Art und nicht `manual_posting`: fuer den
 * Begehungsrhythmus, die Tagesgleichheit und die Historie IST eine
 * Bestandsuebernahme erbrachte Leistung — sie wird nur nicht ein zweites Mal
 * abgerechnet (serviceLogUtils.isBackfilledAltEntry, bewusst NICHT Teil von
 * `isAccountingEntry`).
 */
export const journalKind = (
    entry: { bookingReference?: string | null; description?: string | null } | null | undefined,
): JournalKind => {
    // Ein `kind` AM EINTRAG wird hier bewusst IGNORIERT: die Art wird immer neu
    // aus dem Inhalt abgeleitet. Sonst truege der Rundweg Journal -> Eintrag ->
    // Zeile eine mitgeschleppte (nach einem Spread womoeglich falsche) Art
    // zurueck ins Journal, und die SQL-Fassung (Nr. 60) liefe auseinander.
    const ref = typeof entry?.bookingReference === 'string' ? entry.bookingReference : '';
    if (ref.startsWith('BU-INV-') || ref.startsWith('BU-STO-')) return 'invoice_posting';
    if (ref.startsWith('BU-')) return 'manual_posting';
    if (ref.startsWith('MB-')) return 'manual_posting';
    if (ref.startsWith('VZ-')) return 'waiver';
    if (ref.startsWith('RN-')) return 'reversal';
    if (ref.startsWith('ALT-')) return 'legacy_backfill';
    if (MANUELLE_VERBUCHUNG.test(String(entry?.description ?? ''))) return 'manual_posting';
    if (FLAT_KEY_SHAPE.test(ref)) return 'flat_rate';
    return 'work';
};

/** Dasselbe Muster wie `childIdFromReportRef`, nur mit der Nummer dazu. */
const RPT_CHILD = /^RPT-CHILD-([0-9a-fA-F-]{36})-(\d+)$/;
/** Gruppe 1 GIERIG bis zum LETZTEN Bindestrich — ein sectionId darf welche tragen. */
const RPT_ALLGEMEIN = /^RPT-(.+)-(\d+)$/;

/**
 * Die Alt-Schreibweisen aus Riegel 3 (`unbooked_reports_legacy.sql`).
 *
 * Auf NEUN Stellen begrenzt wie in Nr. 60: eine vierzehnstellige Zahl — ein
 * DATUM, das wie eine Nummer aussieht — liesse dort den int-Cast mit 22003
 * abbrechen.
 */
const NUMMER_IM_TEXT = /(?:#|\bNr\.?\s*)(\d{1,9})(?![0-9]|\.[0-9])/;
/** Geldvorgaenge sind kein Nachweis, dass eine BEGEHUNG stattgefunden hat. */
const GELDBELEG = /^(BU|VZ|RN|MB)-/;

/** Der zerlegte Berichtsbezug einer Zeile. */
export interface JournalReportRef {
    reportNumber: number | null;
    reportSectionId: string | null;
    reportChildProjectId: string | null;
    parseConfidence: JournalParseConfidence;
}

/** Was die Zerlegung ausser dem Ref noch braucht (die Heuristik-Bedingungen). */
export interface JournalRefInput {
    reportRef?: string | null;
    description?: string | null;
    quantity?: number | null;
    bookingReference?: string | null;
}

/**
 * Den Berichtsbezug zerlegen. Die Reihenfolge ist BINDEND.
 *
 * Die Kind-Form passt auch auf die allgemeine Form: `RPT-CHILD-<uuid>-7`
 * erfuellt `^RPT-(.+)-(\d+)$` mit `section = 'CHILD-<uuid>'`. Wird zuerst
 * allgemein geprueft, bekommt jede Tochterbuchung der Flotte einen erfundenen
 * Abschnitt und keine Kind-Kennung — und die Tagespauschalen-Deckung, die
 * genau ueber diese Kennung laeuft, faellt aus (13 Fehlalarme bei 5284-1,
 * 9.775 EUR).
 *
 * ─── Ein KRUMMER Kind-Ref faellt durch BEIDE Zweige ────────────────────────
 * `report_child_project_id` ist in Nr. 59 eine uuid-Spalte, und der CHECK
 * verlangt die echte Form 8-4-4-4-12. Ein `RPT-CHILD-…`, dessen Mitte keine
 * uuid ist, darf deshalb weder als Kind noch — ueber die allgemeine Form — als
 * ABSCHNITT `CHILD-…` durchgehen: das eine scheiterte spaeter am Cast, das
 * andere erfaende einen Abschnitt, den es nie gab. Er landet in „kein Bezug";
 * verloren ist dabei nichts, `report_ref` bleibt die Rohkopie.
 *
 * Passt keine der beiden Formen, entscheidet die Beschreibung — eine NUMMER
 * liefert sie genau in EINER Form: der kanonischen Hauptbuchung (`text_exact`,
 * siehe `ausTextOderHeuristik`). Die Alt-Schreibweisen sagen nur, WIE sicher
 * der Bezug ist (siehe `heuristik`).
 *
 * LOCKSTEP: dieselbe Reihenfolge fuehren Nr. 120
 * (`service_journal_nachspiegeln`) und der CHECK `service_journal_report_ref_chk`
 * (Nr. 119); der Bestand wurde in Nr. 119 nachgezogen.
 */
export const parseReportRef = (input?: JournalRefInput | null): JournalReportRef => {
    const ref = String(input?.reportRef ?? '');
    const ohneBezug: JournalReportRef = {
        reportNumber: null,
        reportSectionId: null,
        reportChildProjectId: null,
        parseConfidence: 'none',
    };

    const kind = RPT_CHILD.exec(ref);
    if (kind) {
        if (!IST_UUID.test(kind[1])) return ausTextOderHeuristik(input, ohneBezug);
        return {
            reportNumber: parseInt(kind[2], 10),
            reportSectionId: null,
            // Kleingeschrieben wie der uuid-Cast in Nr. 60 (`lower(...)::uuid`).
            reportChildProjectId: kind[1].toLowerCase(),
            parseConfidence: 'exact',
        };
    }

    const allgemein = RPT_ALLGEMEIN.exec(ref);
    if (allgemein && !allgemein[1].startsWith('CHILD-')) {
        return {
            reportNumber: parseInt(allgemein[2], 10),
            reportSectionId: allgemein[1],
            reportChildProjectId: null,
            parseConfidence: 'exact',
        };
    }

    return ausTextOderHeuristik(input, ohneBezug);
};

/**
 * Die kanonische Hauptbuchung — `book()` in reportBooking.ts schreibt genau
 * diese Form (`MAIN_BOOKING_DESC`), und `isReportBooked`/`isInspectionBilled`
 * vergleichen sie seit jeher auf GLEICHHEIT. Deshalb verankert (`$`) und ohne
 * fuehrende Null: `Bericht #07 abgeschlossen` ist fuer den Textvergleich kein
 * Bericht 7, also auch hier nicht. Neun Stellen wie ueberall (int-Spalte).
 */
const KANONISCHE_HAUPTBUCHUNG = /^Bericht #(0|[1-9]\d{0,8}) abgeschlossen$/;

/**
 * Ohne zerlegbaren Ref: erst die kanonische Hauptbuchung (`text_exact`), dann
 * die Alt-Schreibweisen (`heuristic`).
 *
 * ─── Warum `text_exact` eine Nummer bekommt und `heuristic` nicht ───────────
 * Gemessen am 23.09.2026 auf PROD: 2.032 Zeilen „Bericht #N abgeschlossen" in
 * 334 Projekten standen ohne `report_number` im Journal — Zeilen aus der Zeit
 * vor dem Ref. Die Nummer ist dort EINDEUTIG (die Form ist verankert und
 * entsteht nur in `book()`), aber die Regel „Nummer nur aus dem Ref" liess sie
 * leer; eine Spaltenpruefung haette 2.032 echte Buchungen nicht gefunden. Die
 * Gefahr, vor der `heuristik` unten schuetzt — eine Einweisung „(Nr. 3)" als
 * Begehung —, besteht bei dieser Form nicht.
 *
 * BEWUSST NICHT die Tochterform `Tochter-Projekt "X": Bericht Nr. N`: ihre
 * Nummer ist die des Berichts der TOCHTER, der Text nennt aber nur den Namen,
 * nicht die Kennung. Eine `report_number` ohne `report_child_project_id` hiesse
 * „eigener Bericht N der Mutter" — genau die Verwechslung, die Riegel (4) in
 * `isInspectionBilled` ausschliesst. Sie bleibt `heuristic`.
 *
 * Nur ein TEXT zaehlt: ein Nicht-Text als Beschreibung (Altbestand) bleibt beim
 * alten Weg — seine Journalfassung ist JSON und passte ohnehin nicht zur
 * SQL-Seite (Nr. 120 liest `->>'description'`).
 */
const ausTextOderHeuristik = (
    input: JournalRefInput | null | undefined,
    ohneBezug: JournalReportRef,
): JournalReportRef => {
    const beschreibung = input?.description;
    const haupt = typeof beschreibung === 'string' ? KANONISCHE_HAUPTBUCHUNG.exec(beschreibung) : null;
    if (haupt) {
        return {
            reportNumber: parseInt(haupt[1], 10),
            reportSectionId: null,
            reportChildProjectId: null,
            parseConfidence: 'text_exact',
        };
    }
    return heuristik(input, ohneBezug);
};

/**
 * Die Alt-Schreibweisen — sie steuern NUR `parse_confidence`, nie eine Nummer.
 *
 * Der Grund ist gemessen und nicht theoretisch: `Einweisung SiGe-Plan (Nr. 3,
 * …)` und `SiGe-Plan exportiert (Plan-Nr. 12, …)` nennen beide eine Nummer und
 * sind beide KEINE Begehung. Wuerde `report_number` daraus gefuellt, zaehlte
 * der dritte `covers_inspection`-Zweig („gilt, wenn `report_number` gesetzt
 * ist") sie als Begehungsnachweis — und eine Einweisung raeumte eine offene
 * Begehungsforderung ab. `report_number` kommt ausschliesslich aus dem
 * zerlegten Ref oder — seit Nr. 119 — aus der kanonischen Hauptbuchung
 * (`ausTextOderHeuristik`), nie aus einer Alt-Schreibweise.
 *
 * Die drei Zusatzbedingungen stammen aus Riegel 3 in
 * `unbooked_reports_legacy.sql`, wo sie am Bestand gemessen wurden: eine Zeile
 * ohne Menge bucht nichts, ein Geldbeleg ist kein Leistungsnachweis, und eine
 * Zusatzleistung ist der KERNFALL, den das Ganze sichtbar machen soll.
 */
const heuristik = (input: JournalRefInput | null | undefined, ohneBezug: JournalReportRef): JournalReportRef => {
    const menge = Number(input?.quantity);
    if (!Number.isFinite(menge) || menge <= 0) return ohneBezug;
    if (GELDBELEG.test(String(input?.bookingReference ?? ''))) return ohneBezug;
    const beschreibung = String(input?.description ?? '');
    if (ZUSATZLEISTUNG.test(beschreibung)) return ohneBezug;
    if (!NUMMER_IM_TEXT.test(beschreibung)) return ohneBezug;
    return { ...ohneBezug, parseConfidence: 'heuristic' };
};

/** Die Hauptbuchung einer Begehung — wie `MAIN_BOOKING_DESC` in reportBooking.ts. */
const HAUPTBUCHUNG = /^Bericht #\d+ abgeschlossen/;
/** Die Kind-zu-Mutter-Buchung — wie `childBookingDescription`. */
const KIND_HAUPTBUCHUNG = /^Tochter-Projekt ".*": Bericht Nr\. \d+/;
/** Zusatzleistungen tragen denselben `reportRef` und beweisen keine Begehung. */
const ZUSATZLEISTUNG = /^Zusatzleistung/;

/**
 * `is_addon` und `covers_inspection` AUS DEM TEXT erschliessen.
 *
 * Das ist der Weg der MIGRATION (Nr. 60): fuer den Bestand gibt es nichts
 * anderes. Der Client nimmt die Aussage stattdessen von der Aufrufstelle
 * entgegen (`JournalEntryStatement`) — die beiden Wege duerfen unterschiedlich
 * ERMITTELN, muessen fuer dieselbe Zeile aber dasselbe ERGEBNIS liefern.
 * Genau das pruefen die Tests.
 *
 * Ein CHECK der Tabelle verbietet `is_addon AND covers_inspection`; die
 * Reihenfolge hier stellt das sicher, weil `is_addon` die Deckung ausschliesst.
 *
 * AUSNAHME von „Menge 0 deckt nicht": die Nachweiszeile auf einer
 * Monatspauschale (`NW-…`, serviceLogUtils.FLAT_PROOF_PREFIX). Sie hat Menge 0
 * per Konstruktion und ist trotzdem DIE Begehung ihres Berichts — mit
 * Berichtsbezug deckt sie wie eine Mengenbuchung, ohne ihn (Handbuchung auf
 * der Pauschale) nicht. `booking_reference` ist optional, damit Aufrufer
 * ohne die Spalte die alte Antwort bekommen.
 */
export const inferInspectionCoverage = (
    row: Pick<ServiceJournalRow, 'kind' | 'quantity' | 'description' | 'report_number'>
        & Partial<Pick<ServiceJournalRow, 'booking_reference'>>,
): { isAddon: boolean; coversInspection: boolean } => {
    const description = String(row.description ?? '');
    const isAddon = ZUSATZLEISTUNG.test(description);
    if (isAddon) return { isAddon: true, coversInspection: false };
    const nachweis = isFlatProofEntry({ bookingReference: row.booking_reference });
    if (row.kind !== 'work' || (row.quantity === 0 && !nachweis)) return { isAddon: false, coversInspection: false };

    if (HAUPTBUCHUNG.test(description)) return { isAddon: false, coversInspection: true };
    if (KIND_HAUPTBUCHUNG.test(description)) return { isAddon: false, coversInspection: true };

    // Eine Nummer ohne eine der beiden Formen: die Alt-Schreibweisen aus
    // Riegel 3 („Bericht #14 versendet (Cloud)", „Bericht Nr. 44"). 91 % der
    // Liste aus Nr. 45 waren genau diese Zeilen — sie decken sehr wohl, und
    // wer sie „nachbucht", bucht die Begehung ein zweites Mal.
    return { isAddon: false, coversInspection: row.report_number !== null };
};

/** `forced` — die ausdruecklich uebersteuerte Buchung. */
const ERZWUNGEN = /\(ERZWUNGEN\)/;

// ═══════════════════════════════════════════════════════════════════════════
// Die deterministische Ersatz-id
// ═══════════════════════════════════════════════════════════════════════════

const MD5_K: number[] = [];
for (let i = 0; i < 64; i++) MD5_K.push(Math.floor(Math.abs(Math.sin(i + 1)) * 4294967296));
const MD5_S = [
    7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22,
    5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20,
    4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23,
    6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21,
];

/**
 * MD5 als Hex — die JS-Entsprechung von Postgres `md5(text)`.
 *
 * Selbst gebaut und nicht ueber `crypto.subtle`: die Ersatz-id muss SYNCHRON
 * entstehen (die Abbildung ist eine reine Funktion und wird auch aus
 * Ableitungen gerufen), und WebCrypto kennt md5 ohnehin nicht. sha256 waere
 * die bessere Prueffunktion, hilft hier aber nichts — massgeblich ist, dass
 * Client und Migration DENSELBEN Schluessel errechnen, und in Postgres ist
 * md5 die Funktion, die ohne Erweiterung zur Verfuegung steht.
 */
export const md5Hex = (input: string): string => {
    const bytes = new TextEncoder().encode(input);
    const bitLen = bytes.length * 8;
    const padded = new Uint8Array(((((bytes.length + 8) >> 6) + 1) * 64));
    padded.set(bytes);
    padded[bytes.length] = 0x80;
    const view = new DataView(padded.buffer);
    view.setUint32(padded.length - 8, bitLen >>> 0, true);
    view.setUint32(padded.length - 4, Math.floor(bitLen / 4294967296), true);

    let a0 = 0x67452301, b0 = 0xefcdab89, c0 = 0x98badcfe, d0 = 0x10325476;
    const m = new Array<number>(16);
    for (let chunk = 0; chunk < padded.length; chunk += 64) {
        for (let j = 0; j < 16; j++) m[j] = view.getUint32(chunk + j * 4, true);
        let a = a0, b = b0, c = c0, d = d0;
        for (let i = 0; i < 64; i++) {
            let f: number;
            let g: number;
            if (i < 16) { f = (b & c) | (~b & d); g = i; }
            else if (i < 32) { f = (d & b) | (~d & c); g = (5 * i + 1) % 16; }
            else if (i < 48) { f = b ^ c ^ d; g = (3 * i + 5) % 16; }
            else { f = c ^ (b | ~d); g = (7 * i) % 16; }
            const sum = (f + a + MD5_K[i] + m[g]) | 0;
            const s = MD5_S[i];
            a = d;
            d = c;
            c = b;
            b = (b + ((sum << s) | (sum >>> (32 - s)))) | 0;
        }
        a0 = (a0 + a) | 0;
        b0 = (b0 + b) | 0;
        c0 = (c0 + c) | 0;
        d0 = (d0 + d) | 0;
    }

    const hexLE = (n: number): string => {
        let out = '';
        for (let i = 0; i < 4; i++) out += ((n >>> (i * 8)) & 0xff).toString(16).padStart(2, '0');
        return out;
    };
    return hexLE(a0) + hexLE(b0) + hexLE(c0) + hexLE(d0);
};

/** Die Form einer uuid — genau das, was Postgres als `uuid` annimmt. */
const IST_UUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * Die Menge — vorzeichenbehaftet, 0 ist gueltig, Unlesbares ist `null`.
 *
 * Bewusst KEINE erfundene 0: der serviceLog steht unter keinem CHECK, und ein
 * krummer Wert kommt vor. Ihn zu 0 zu machen hiesse, eine Zahl zu behaupten,
 * die niemand geschrieben hat — die Migration gibt eine solche Zeile
 * stattdessen in die Quarantaene (`nicht_abbildbar`). Ein ehrlicher
 * Quarantaeneeintrag ist besser als eine erfundene Null; hier ist die
 * Entsprechung, die Zeile gar nicht erst abzubilden (`isJournalMappable`).
 */
const journalQuantity = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : null;
};

/**
 * Laesst sich diese Zeile ueberhaupt abbilden?
 *
 * Die Entsprechung des fuenften Quarantaenegrundes `nicht_abbildbar` aus
 * Nr. 60: keine `serviceId`, keine als Zahl lesbare `quantity`, kein aus
 * `date` ableitbarer Kalendertag. Solche Zeilen gehen NICHT ins Journal —
 * ein Payload mit einer NOT-NULL-Verletzung risse sonst den ganzen Append mit.
 *
 * S4b muss sie sichtbar machen (Protokoll oder Befund), nicht verschlucken:
 * der Pruefsatz der ganzen Umstellung lautet, dass keine serviceLog-Zeile am
 * Ende weder im Journal noch in der Quarantaene steht.
 */
export const isJournalMappable = (entry: ServiceTransaction | null | undefined): boolean =>
    !!entry
    && String(entry.serviceId ?? '').trim() !== ''
    && journalQuantity(entry.quantity) !== null
    && entryDay(entry.date) !== null;

/**
 * Die kanonische Textform einer Zahl — verabredet mit der SQL-Seite.
 *
 * TypeScript `String(Number(x))` gegen SQL `trim_scale(x::numeric)::text`.
 * Beide liefern fuer dieselbe Zahl dieselbe Zeichenkette: `1.50` und `1.5`
 * werden zu `1.5`, `1.0` und `1` zu `1`, `-2.500` zu `-2.5`. Wer eine dritte
 * Schreibweise braucht, aendert BEIDE Zeilen oder keine.
 */
export const canonicalNumber = (value: number): string => String(Number(value));

/** Kanonische Zahl oder LEERE Zeichenkette — `coalesce(<x>_kanonisch, '')`. */
const kanonischOderLeer = (value: unknown): string => {
    const n = journalQuantity(value);
    return n === null ? '' : canonicalNumber(n);
};

/** Kennung kleingeschrieben oder leer — `coalesce(lower(<x>), '')`. */
const kleinOderLeer = (value: unknown): string => String(value ?? '').trim().toLowerCase();

/**
 * Der deterministische Ersatzschluessel — `md5(...)::uuid` wie in Nr. 60.
 *
 * NIE zufaellig: ein zweiter Migrationslauf legte dieselbe Zeile sonst ein
 * zweites Mal an, und ein `on conflict do nothing` faenge sie nicht.
 *
 * ─── Der Schluessel haengt am INHALT, NIE an der Arrayposition ─────────────
 * Eine Zwischenfassung der Spezifikation leitete ihn aus `project_id` und dem
 * Arrayindex ab. Das ist zurueckgenommen, und der Grund ist der teuerste
 * Fehler, den diese Umstellung machen koennte: viele Schreibpfade haengen
 * vorne an, das Array verschiebt sich also staendig. Dieselbe abgeleitete id
 * bezeichnete nach einer Verschiebung eine ANDERE Buchung, `on conflict do
 * nothing` verwuerfe die neue Zeile still, und eine Leistung waere
 * uebersprungen, doppelt gezaehlt oder der falschen Buchung zugeordnet.
 *
 * Aus dem Inhalt abgeleitet ist die id stabil gegen jede Umsortierung. Dass
 * zwei inhaltlich identische Zeilen denselben Schluessel bekommen, ist kein
 * Mangel, sondern die richtige Antwort: die zweite IST eine Dublette und
 * gehoert in die Quarantaene, nicht ins Journal.
 *
 * ─── Die Regel, welche Felder hineingehoeren ───────────────────────────────
 * JEDES Feld, in dem sich zwei Zeilen unterscheiden koennen, ohne dieselbe
 * Buchung zu sein. Ein Feld wegzulassen heisst zu behaupten, es unterscheide
 * nichts.
 *
 * Genau daran fehlten vier: bei einer Teilrechnung mit mehreren Auftraggebern
 * (Nr. 51/54) schreibt `invoiceWriteBackLogic` JE PARTEI eine eigene
 * Protokollzeile — gleicher Beleg, gleiche Position, gleiches Datum, gleiche
 * Beschreibung, bei haelftiger Teilung auch gleiche Menge. Ohne `customerId`
 * im Schluessel waeren sie inhaltsgleich; eine ginge als Dublette in die
 * Quarantaene, `projectSplit.billedByParty` laese den Stand EINER Partei zu
 * niedrig, und die naechste kumulative Rechnung forderte die Menge ein zweites
 * Mal — die 6172-Klasse.
 *
 * `splitShareBp` bleibt DRAUSSEN: er ist eine Ableitung des Anteils, keine
 * eigene Buchung. Zwei Zeilen, die sich nur darin unterschieden, waeren ein
 * Datenfehler und keine zwei Leistungen.
 *
 * Reihenfolge und Trennzeichen sind bindend und stehen ebenso in Nr. 60.
 */
export const deterministicEntryId = (input: {
    projectId: string;
    date: string;
    serviceId: string;
    quantity: number;
    description?: string | null;
    bookingReference?: string | null;
    reportRef?: string | null;
    customerId?: string | null;
    splitPartyId?: string | null;
    appliedUsed?: number | null;
    appliedBilled?: number | null;
}): string => {
    const roh = [
        input.projectId,
        input.date,
        input.serviceId,
        canonicalNumber(input.quantity),
        input.description ?? '',
        input.bookingReference ?? '',
        input.reportRef ?? '',
        // Kleingeschrieben: eine uuid in Grossschreibung erzeugte sonst
        // denselben Fehler wie eine uneinheitliche Zahlenform.
        kleinOderLeer(input.customerId),
        kleinOderLeer(input.splitPartyId),
        kanonischOderLeer(input.appliedUsed),
        kanonischOderLeer(input.appliedBilled),
    ].join(':');
    const hex = md5Hex(roh);
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
};

/**
 * Behaelt der Eintrag seine id — und wenn nicht, warum nicht?
 *
 * Vier Faelle, jeder mit einer Zeile in `service_journal_unkeyed`. Der Rohwert
 * wandert nach `legacy_entry_id` und ist damit NICHT verloren: die Zuordnungen
 * (`allocations.entryId`) zeigen auf ihn, und ohne ihn faende die
 * Rueckabbildung ihren Eintrag nicht wieder.
 *
 * `doppelt_global` kann der Client nicht sehen — er kennt nur sein eigenes
 * Projekt. Das ist Sache der Migration. Ebenso die Quarantaene selbst: der
 * Client hat keinen Schreibweg dorthin, er meldet den Grund nur.
 *
 * ─── AUS DEM JOURNAL GELESENE TRANSAKTIONEN NIE ZURUECKSPIEGELN ───────────
 * Der Ersatzschluessel hasht das Datum als ROHTEXT (wie Nr. 60,
 * `datum_roh`). Eine Transaktion aus `journalToServiceTransactions` traegt
 * aber nicht mehr den JSON-Text, sondern die Berliner Schreibweise
 * (`2026-08-01T00:00:00+02:00`, V3) — vor V3 den UTC-Text aus PostgREST, der
 * ebenso abwich. Ginge sie noch einmal durch `toJournalRows`, bekaeme jede
 * Zeile mit abgeleitetem Schluessel (JSON-id keine uuid oder doppelt) einen
 * ANDEREN Schluessel, und `on conflict do nothing` griffe nicht: eine zweite
 * lebende Zeile, dieselbe Leistung doppelt. Nur Zeilen, die ihre uuid
 * behalten, kaemen unveraendert an.
 *
 * Normalisieren laesst sich das hier nicht: dieselbe Berliner Mitternacht
 * steht im JSON als `2026-08-01`, `2026-07-31T22:00:00.000Z` oder
 * `2026-08-01T00:00:00` und ergibt heute drei verschiedene Schluessel — aus
 * dem gelesenen Zeitpunkt ist der Rohtext nicht zurueckzugewinnen, und jede
 * andere Hashform liefe gegen Nr. 60 (die Nachspiegelung Nr. 76 schluesselt
 * ueber die JSON-id und hasht nicht). Gespiegelt werden deshalb NUR
 * JSON-Eintraege. Belegt in serviceJournal.test.ts („aus dem Journal
 * gelesen, nie zurueckgespiegelt").
 */
export const journalIdDecision = (
    entry: ServiceTransaction,
    projectId: string,
    vergeben: Set<string>,
): JournalIdDecision => {
    const roh = typeof entry.id === 'string' ? entry.id.trim() : '';
    const ersatz = deterministicEntryId({
        projectId,
        date: String(entry.date ?? ''),
        serviceId: String(entry.serviceId ?? '').trim(),
        quantity: journalQuantity(entry.quantity) ?? 0,
        description: entry.description,
        bookingReference: entry.bookingReference,
        reportRef: entry.reportRef,
        customerId: entry.customerId,
        splitPartyId: entry.splitPartyId,
        appliedUsed: entry.appliedUsed,
        appliedBilled: entry.appliedBilled,
    });

    // Eine brauchbare, noch freie id behaelt der Eintrag.
    if (roh && IST_UUID.test(roh) && !vergeben.has(roh)) {
        return { id: roh, legacyEntryId: null, reason: null };
    }

    const warum: JournalUnkeyedReason = !roh
        ? 'keine_id'
        : (!IST_UUID.test(roh) ? 'keine_uuid' : 'doppelt_im_projekt');

    return {
        id: ersatz,
        legacyEntryId: roh || null,
        // Traegt der Ersatzschluessel schon jemand, ist der Inhalt beider
        // Zeilen derselbe — eine ECHTE Dublette. Das ist keine Kollision,
        // sondern die richtige Antwort: die zweite gehoert in die Quarantaene,
        // nicht als zweite Leistung ins Journal. Der Grund schlaegt den
        // Schluesselgrund, weil er der schwerere ist.
        reason: vergeben.has(ersatz) ? 'doppelt_im_projekt' : warum,
    };
};

/** `undefined` und `''` gehen als `null` in die Spalte — wie in jsonb. */
const orNull = (value: unknown): string | null => {
    if (value === null || value === undefined) return null;
    const s = String(value);
    return s === '' ? null : s;
};

/** Wie `orNull`, zusaetzlich beschnitten — `nullif(btrim(…), '')` in Nr. 60. */
const trimOrNull = (value: unknown): string | null => orNull(String(value ?? '').trim());

/**
 * Nur eine echte uuid, kleingeschrieben — sonst `null`.
 *
 * `booked_by_id` ist in Nr. 59 eine uuid-Spalte. Im Bestand steht dort auch
 * `'system'` und aehnliches; das ist kein Verlust, sondern ein Wert, der nie
 * auf ein Profil gezeigt hat.
 */
const uuidOrNull = (value: unknown): string | null => {
    const s = String(value ?? '').trim();
    return IST_UUID.test(s) ? s.toLowerCase() : null;
};

/** Zahl oder `null` — fuer `applied_used`/`applied_billed`. */
const numOrNull = (value: unknown): number | null => {
    if (value === null || value === undefined) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

// ═══════════════════════════════════════════════════════════════════════════
// Die Abbildung
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `ServiceTransaction` → Journalzeile. Der Kern dieses Moduls.
 *
 * Rein: kein Netz, kein Zufall, keine Uhr. Zweimal mit demselben Eingang
 * gerufen kommt zeichengleich dasselbe heraus — sonst waere die
 * Abweichungssicht nicht auswertbar.
 *
 * Nicht abbildbare Zeilen werden UEBERSPRUNGEN und nicht mit erfundenen
 * Werten gefuellt (`isJournalMappable`). Wer sie braucht, filtert selbst und
 * meldet sie — verschweigen darf S4b sie nicht.
 */
export const toJournalRows = (input: JournalMappingInput): ServiceJournalRow[] => {
    const { projectId, entries, source } = input;
    const vergeben = new Set<string>(input.knownIds ?? []);
    const statements = input.statements ?? {};

    return (entries ?? []).filter(isJournalMappable).map(entry => {
        const schluessel = journalIdDecision(entry, projectId, vergeben);
        // Beide Werte werden vorgemerkt: die vergebene id und der Rohwert.
        // Sonst behielte die DRITTE Zeile derselben doppelten id wieder ihre
        // eigene — die Dublette waere nur um eine Zeile verschoben.
        if (schluessel.legacyEntryId) vergeben.add(schluessel.legacyEntryId);
        vergeben.add(schluessel.id);

        const quantity = journalQuantity(entry.quantity) as number;
        const description = orNull(entry.description);
        const kind = journalKind(entry);
        const bezug = parseReportRef(entry);
        const tag = entryDay(entry.date);

        // Die Aussage der Aufrufstelle hat Vorrang; fehlt sie, wird sie aus
        // dem Text erschlossen — derselbe Weg wie in der Migration.
        const erschlossen = inferInspectionCoverage({
            kind,
            quantity,
            description,
            report_number: bezug.reportNumber,
            booking_reference: orNull(entry.bookingReference),
        });
        const aussage = statements[String(entry.id ?? '')] ?? {};
        const isAddon = aussage.isAddon ?? erschlossen.isAddon;
        const coversInspection = isAddon
            ? false
            : (aussage.coversInspection ?? erschlossen.coversInspection);

        return {
            id: schluessel.id,
            project_id: projectId,
            service_id: String(entry.serviceId ?? '').trim(),
            quantity,
            entry_at: String(entry.date ?? ''),
            entry_day: tag,
            kind,
            report_ref: orNull(entry.reportRef),
            report_number: bezug.reportNumber,
            report_section_id: bezug.reportSectionId,
            report_child_project_id: bezug.reportChildProjectId,
            parse_confidence: bezug.parseConfidence,
            is_addon: isAddon,
            covers_inspection: coversInspection,
            forced: ERZWUNGEN.test(String(entry.description ?? '')),
            booking_reference: orNull(entry.bookingReference),
            description,
            performer_id: orNull(entry.performerId),
            performer_name: orNull(entry.performerName),
            is_substitution: entry.isSubstitution === true,
            booked_at: orNull(entry.bookedAt),
            booked_by: orNull(entry.bookedBy),
            booked_by_id: uuidOrNull(entry.bookedById),
            customer_id: trimOrNull(entry.customerId),
            split_party_id: trimOrNull(entry.splitPartyId),
            split_share_bp: numOrNull(entry.splitShareBp),
            applied_used: numOrNull(entry.appliedUsed),
            applied_billed: numOrNull(entry.appliedBilled),
            // ─── Die Ruecknahmespur, wortgleich (SQL Nr. 116/117) ──────────
            // Nichts wird abgeleitet: eine Spur OHNE diese Felder ist eine
            // Ruecknahme ohne Eintrag (Luecke) oder eine aus der Zeit vor dem
            // Feld, und beides ist eine Aussage. Ein erfundener Wert waere hier
            // besonders teuer — `reversedQuantity` ist eine Menge, mit der der
            // Dialog rechnet (`partiallyReversed`).
            //
            // BIS SQL Nr. 117 GELAUFEN IST, kommen die drei Schluessel nicht im
            // Journal an, und das ist der geplante Zwischenstand: vor Nr. 116
            // verwirft `jsonb_populate_recordset(null::service_journal, …)` sie
            // als unbekannte Schluessel, zwischen Nr. 116 und Nr. 117 laesst die
            // alte (abschliessende) Insert-Spaltenliste der RPC sie weg. Beides
            // ist still und folgenlos — der Client darf deshalb VOR dem SQL
            // ausgeliefert werden. Die Spuren, die in dieser Zeit ohne die drei
            // Werte ins Journal laufen, waeren spaeter per Nachspiegelung
            // nachzutragen (Muster Nr. 76); hier wird bewusst KEIN Backfill
            // gebaut, und vor S5c liest der Dialog ohnehin aus dem JSON.
            reversed_entry_id: trimOrNull(entry.reversedEntryId),
            reversed_report_ref: trimOrNull(entry.reversedReportRef),
            reversed_quantity: numOrNull(entry.reversedQuantity),
            billing_mode_at_entry: billingModeAt(input.linkedProjectConfig, tag) ?? null,
            source,
            legacy_entry_id: schluessel.legacyEntryId,
        };
    });
};

/**
 * Die Rueckabbildung — damit jeder bestehende Verbraucher weiter seine
 * gewohnte Form bekommt.
 *
 * Sie ist der eigentliche Grund, warum die Umstellung in Stufen gehen kann:
 * solange das Journal als `ServiceTransaction[]` herauskommt, muss keine der
 * rund 65 Auspackstellen angefasst werden, um von ihm zu lesen.
 *
 * Die id kommt aus `legacy_entry_id`, sobald der Eintrag eine hat. Sonst
 * verloere die Zuordnung (`allocations.entryId`) ihren Anker — und das ist der
 * eine Schritt des Plans, der nur mit einer Gutschrift ruecknehmbar waere.
 *
 * Zurueckgenommene Zeilen (`voided_at`) gehoeren nicht mehr ins Protokoll.
 *
 * ─── Das Datum in BERLINER Schreibweise (V3 vor S5a) ──────────────────────
 * `entry_at` ist `timestamptz`, und PostgREST gibt es in UTC heraus. Ein im
 * JSON reiner Tag `2026-08-01` ist im Journal die Berliner Mitternacht
 * (Nr. 60/103) und kaeme als `2026-07-31T22:00:00+00:00` zurueck — jeder
 * `asDay`-Leser (Zuordnungstag, Leistungszeitraum, DATEV-Monat, `inPeriod`,
 * `openByMonth`) saehe den Vortag, am Monatsersten den Vormonat.
 * `berlinerZeitstempel` schreibt denselben Moment als Berliner Wanduhrzeit
 * mit Versatz (`2026-08-01T00:00:00+02:00`): `new Date()` liest denselben
 * Zeitpunkt, der Textschnitt den Tag aus `entry_day`.
 *
 * Was sich dadurch NICHT aendert: jeder Vergleich ueber `Date.parse`, die
 * Reihenfolge nach `entry_at` (bis auf die doppelte Herbststunde, siehe
 * `berlinerZeitstempel`; der Schattenvergleich wertet sie als Unentschieden,
 * allocationShadow.ts). Was sich aendert: ein TEXTvergleich gegen einen
 * Zeitstempel aus ANDERER Quelle (`e.date > beleg.createdAt`, UTC) waere um
 * den Versatz falsch; ein kuenftiger Journal-Leser vergleicht Zeitpunkte
 * ueber `Date.parse` und Tage ueber `asDay`. Stand V3 tut das keiner: der
 * einzige Leser ausserhalb der Tests ist der Schattenvergleich.
 * Ohne Zone oder unlesbar bleibt der Rohwert stehen, wie bisher.
 *
 * ─── Die Ausgabe NIE wieder ins Journal spiegeln ──────────────────────────
 * Nie als `entries` von `mirrorEntriesToJournal`/`toJournalRows`: das Datum
 * ist nicht mehr der JSON-Rohtext, auf dem der Ersatzschluessel beruht — eine
 * Zeile mit abgeleitetem Schluessel kaeme als zweite lebende Zeile an
 * (Begruendung an `journalIdDecision`). `previousLog` liefert dort nur
 * Kennungen (`knownIds`), das Datum spielt fuer ihn keine Rolle.
 */
export const journalToServiceTransactions = (
    rows: ServiceJournalRow[] | null | undefined,
): ServiceTransaction[] =>
    (rows ?? [])
        .filter(row => !row.voided_at)
        .map(row => {
            const tx: ServiceTransaction = {
                id: row.legacy_entry_id ?? row.id,
                date: berlinerZeitstempel(row.entry_at) ?? row.entry_at,
                serviceId: row.service_id,
                quantity: row.quantity,
                performerId: row.performer_id ?? '',
                performerName: row.performer_name ?? '',
                isSubstitution: row.is_substitution === true,
            };
            // Die Buchungsart kommt als SPALTE zurueck und wird nicht aus
            // Praefix und Text neu erschlossen — genau der Sinn der
            // Umstellung (serviceLogUtils.isPostingEntry liest sie mit
            // Vorrang). Sie steht auf JEDER Zeile (NOT NULL in Nr. 59).
            if (row.kind) tx.kind = row.kind;
            // Nur belegte Felder werden gesetzt: ein `description: undefined`
            // im Objekt ist beim Vergleich gegen das JSON nicht dasselbe wie
            // ein fehlendes Feld, und die Abweichungssicht vergliche Rauschen.
            if (row.description !== null) tx.description = row.description;
            if (row.booked_at !== null) tx.bookedAt = row.booked_at;
            if (row.booked_by !== null) tx.bookedBy = row.booked_by;
            if (row.booked_by_id !== null) tx.bookedById = row.booked_by_id;
            if (row.booking_reference !== null) tx.bookingReference = row.booking_reference;
            if (row.report_ref !== null) tx.reportRef = row.report_ref;
            if (row.customer_id !== null) tx.customerId = row.customer_id;
            // Der Aufteilungsschluessel je Abschnitt. Faellt er auf dem
            // Rueckweg weg, loest `projectSplit.billedByParty` nur noch ueber
            // den Auftraggeber auf — das sperrt bei Mehrdeutigkeit oder ordnet
            // falsch zu, und beides ist geldrelevant.
            if (row.split_party_id != null) tx.splitPartyId = row.split_party_id;
            if (row.split_share_bp != null) tx.splitShareBp = row.split_share_bp;
            if (row.applied_used !== null) tx.appliedUsed = row.applied_used;
            if (row.applied_billed !== null) tx.appliedBilled = row.applied_billed;
            // ─── Die Ruecknahmespur zurueck (SQL Nr. 116/117) ──────────────
            // `!= null` und nicht `!== null`: eine Zeile, die VOR Nr. 116
            // geladen wurde, hat die Spalten gar nicht, und `row.x` ist dann
            // `undefined`. Mit dem strengen Vergleich entstuende
            // `reversedEntryId: undefined` als eigenes Feld — beim Vergleich
            // gegen das JSON ist das nicht dasselbe wie ein fehlendes Feld, und
            // die Abweichungssicht vergliche Rauschen.
            if (row.reversed_entry_id != null) tx.reversedEntryId = row.reversed_entry_id;
            if (row.reversed_report_ref != null) tx.reversedReportRef = row.reversed_report_ref;
            if (row.reversed_quantity != null) tx.reversedQuantity = row.reversed_quantity;
            return tx;
        });

// ═══════════════════════════════════════════════════════════════════════════
// Datenschicht
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Fehlt die Funktion in der Datenbank? Wortgleich aus
 * `cloudService.isMissingRpc` — der Client wird VOR dem SQL ausgeliefert.
 */
const isMissingRpc = (error: any): boolean =>
    error?.code === 'PGRST202' || /could not find the function/i.test(error?.message || '');

/**
 * Fehlt die TABELLE? PostgREST meldet das mit `PGRST205`, Postgres selbst mit
 * `42P01`. Derselbe Fall wie oben, nur fuer das Lesen.
 */
const isMissingTable = (error: any): boolean =>
    error?.code === 'PGRST205'
    || error?.code === '42P01'
    || /could not find the table|relation .* does not exist/i.test(error?.message || '');

/** Warum ein Append nicht geschrieben hat. */
export type JournalAppendReason = 'leer' | 'rpc_fehlt' | 'fehler';

export interface JournalAppendResult {
    written: boolean;
    reason?: JournalAppendReason;
    error?: unknown;
}

/**
 * Zeilen an das Journal anhaengen (`service_journal_append`, SQL Nr. 61).
 *
 * ─── Diese Funktion wirft NIE ──────────────────────────────────────────────
 * Sie ist die zweite Haelfte eines Doppelschreibens, dessen erste Haelfte —
 * das JSON — zu diesem Zeitpunkt bereits geschrieben ist. Die Reihenfolge ist
 * fest: JSON zuerst, Journal danach, getrennte Transaktionen. Ein Wurf hier
 * risse den Aufrufer aus einem Pfad, in dem die Leistung laengst gebucht ist,
 * und der Anwender saehe einen Fehler zu einer Buchung, die gelungen ist.
 * Solange das Journal Zweitschrift ist, ist sein Ausfall folgenlos; ab dem
 * Zeitpunkt, an dem es fuehrt, faengt die Abweichungssicht den Rest.
 *
 * Die fehlende RPC ist dabei der ERWARTETE Fall und kein Vorfall: auf einer
 * Umgebung ohne Nr. 61 verhaelt sich die App byte-gleich wie heute.
 */
export const appendToJournal = async (
    projectId: string,
    rows: ServiceJournalRow[],
): Promise<JournalAppendResult> => {
    const id = (projectId || '').trim();
    if (!id || !rows || rows.length === 0) return { written: false, reason: 'leer' };

    try {
        const { error } = await supabase.rpc('service_journal_append', {
            p_project_id: id,
            p_entries: rows,
        });
        if (!error) return { written: true };
        if (isMissingRpc(error)) {
            // Bewusst kein console.warn: das ist der geplante Zustand zwischen
            // Client-Deploy und SQL-Lauf und darf das Protokoll nicht fluten.
            return { written: false, reason: 'rpc_fehlt', error };
        }
        console.warn('[serviceJournal] Anhaengen fehlgeschlagen:', error);
        void meldeSpiegelFehler(id, rows, error);
        return { written: false, reason: 'fehler', error };
    } catch (e) {
        console.warn('[serviceJournal] Anhaengen fehlgeschlagen:', e);
        void meldeSpiegelFehler(id, rows, e);
        return { written: false, reason: 'fehler', error: e };
    }
};

/**
 * Einen gescheiterten Spiegelvorgang in `bug_reports` melden — sichtbar im
 * Admin-Bereich, nicht nur in der Konsole des Geraets.
 *
 * Warum: am 11.09.2026 fehlte 6151 #22 im Journal, und vom Server aus war
 * nicht zu sehen, WARUM (Sitzung, Browser, Antwort der RPC). `console.warn`
 * sieht niemand. Die Meldung traegt einen stabilen Text (Fingerabdruck =
 * Fehlercode + Meldung) und den Rest als Kontext: Projekt, Quelle, Zeilen,
 * Netz, Korb. Der Kanal drosselt selbst (5 min je Fingerabdruck, 20 je
 * Sitzung) und darf nie werfen — nur im Browser, nie in Tests.
 *
 * Exportiert fuer die teilweise Ruecknahme (serviceReversalService): eine
 * gekuerzte Zeile, die wegen einer doppelten id NICHT ersetzt wird, meldet
 * sich ueber denselben Kanal.
 */
export const meldeSpiegelFehler = async (
    projectId: string,
    rows: ServiceJournalRow[],
    error: unknown,
    // Welcher Schreibweg gescheitert ist — Teil des Fingerabdrucks, damit ein
    // gescheitertes Ersetzen (Nr. 136) nicht unter „Anhaengen" einsortiert wird.
    vorgang: 'Anhaengen' | 'Ersetzen' = 'Anhaengen',
): Promise<void> => {
    if (typeof window === 'undefined') return;
    try {
        const e = (error ?? {}) as { code?: string; message?: string; details?: string; hint?: string; stack?: string };
        const code = e.code ? String(e.code) : 'ohne_code';
        const text = (e.message ?? String(error)).slice(0, 200);
        const kontext = {
            projectId,
            zeilen: rows.length,
            source: rows[0]?.source ?? null,
            reportRefs: rows.map(r => r.report_ref).filter(Boolean).slice(0, 5),
            details: e.details ?? null,
            hint: e.hint ?? null,
            online: typeof navigator !== 'undefined' ? navigator.onLine : null,
            korb: journalMirrorOutboxStore() ? 'indexeddb' : 'nur_speicher',
            userId: await aktuelleUserId(),
        };
        // Der Hintergrundkanal, nicht `captureBug`: dessen Sperre verwarf eine
        // Meldung, solange eine andere unterwegs war, und jeder neue
        // Fingerabdruck kostete einen Gemini-Vorschlag.
        const { meldeImHintergrund } = await import('./bugReportService');
        await meldeImHintergrund({
            message: `[serviceJournal] ${vorgang} fehlgeschlagen: ${code} ${text}`,
            stack: `${e.stack ?? ''}\n${JSON.stringify(kontext)}`,
            source: 'window',
        });
    } catch {
        // Die Meldung ist eine Zugabe: sie darf den Schreibpfad nie beeinflussen.
    }
};

/**
 * Das Blaettern ueber die Journalzeilen EINES Projekts — die gemeinsame
 * Grundlage von `loadJournal` (alle Spalten) und
 * `ladeBuchungsartenInsRegister` (`REGISTER_SPALTEN`).
 *
 * `null` heisst „kein Journal bekannt" und ist ausdruecklich KEIN Fehler:
 * fehlende Tabelle, kein Netz, keine Berechtigung. Ein LEERES Array heisst
 * dagegen „das Journal kennt dieses Projekt und es hat keine Zeilen" — die
 * Unterscheidung ist der ganze Punkt: waeren beide Faelle `[]`, saehe ein
 * Projekt mit ausgefallener Verbindung wie eines ohne jede Leistung aus, und
 * genau daraus wuerde eine Rechnung ueber nichts.
 */
const journalSeitenweise = async <T>(projectId: string, spalten: string): Promise<T[] | null> => {
    const id = (projectId || '').trim();
    if (!id) return null;
    try {
        // SEITENWEISE, und das ist keine Vorsicht auf Vorrat: PostgREST liefert
        // hoechstens 1000 Zeilen und sagt NICHT, dass es gekappt hat (dieselbe
        // Falle wie bei der Nutzeraktivitaet). Ein Projekt mit mehr
        // Journalzeilen saehe sonst aus wie eines mit genau 1000 — beim Lesen
        // fehlten Leistungen, und die Ruecknahme hielte die gekappten Zeilen
        // fuer „nicht im Journal" und liesse sie stehen.
        const seite = 1000;
        const alle: T[] = [];
        for (let von = 0; ; von += seite) {
            const { data, error } = await supabase
                .from('service_journal')
                .select(spalten)
                .eq('project_id', id)
                .order('entry_at', { ascending: true })
                // Zweiter Schluessel: `entry_at` allein ist nicht eindeutig (reine
                // Tage, ALT-Zeilen). Ohne ihn darf PostgreSQL gleichrangige Zeilen
                // je Seite anders ordnen — an der Seitengrenze fehlte dann eine
                // oder kaeme doppelt. Seit Stufe 3b sperrte das Tor daran.
                .order('id', { ascending: true })
                .range(von, von + seite - 1);
            if (error) {
                if (!isMissingTable(error)) console.warn('[serviceJournal] Lesen fehlgeschlagen:', error);
                return null;
            }
            if (!Array.isArray(data)) return von === 0 ? null : alle;
            alle.push(...(data as unknown as T[]));
            if (data.length < seite) return alle;
        }
    } catch (e) {
        console.warn('[serviceJournal] Lesen fehlgeschlagen:', e);
        return null;
    }
};

/**
 * Die Journalzeilen eines Projekts lesen — ALLE Spalten, einschliesslich der
 * zurueckgenommenen Zeilen. `null` heisst „kein Journal bekannt"; der
 * Aufrufer faellt dann auf das JSON zurueck (`getServiceLog`).
 */
export const loadJournal = (projectId: string): Promise<ServiceJournalRow[] | null> =>
    journalSeitenweise<ServiceJournalRow>(projectId, '*');

/**
 * Was das Buchungsart-Register von einer Journalzeile braucht — mehr nicht:
 * die Art (S5b) und den Berichtsbezug samt Fingerabdruck (S5c,
 * serviceLogUtils.berichtsbezugVon).
 */
type BuchungsartZeile = Pick<
    ServiceJournalRow,
    | 'id' | 'legacy_entry_id' | 'kind' | 'voided_at'
    | 'report_ref' | 'description' | 'booking_reference' | 'quantity' | 'report_number'
    | 'report_section_id' | 'report_child_project_id' | 'parse_confidence' | 'covers_inspection'
>;

/** Die Spaltenauswahl dazu — exportiert fuer den Test, der sie gegen den Typ prueft. */
export const REGISTER_SPALTEN =
    'id, legacy_entry_id, kind, voided_at, report_ref, description, booking_reference, quantity, '
    + 'report_number, report_section_id, report_child_project_id, parse_confidence, covers_inspection';

/**
 * Die Buchungsarten eines Projekts EINMAL ins Register holen — der Weg, auf
 * dem ein Eintrag aus dem Projekt-JSON zu seiner Journal-Spalte kommt, ohne
 * dass das JSON angefasst wird (serviceLogUtils, Buchungsart-Register).
 *
 * `null` heisst wie bei `loadJournal` „kein Journal bekannt" (Tabelle fehlt,
 * offline, keine Berechtigung). Dann bleibt das Register UNVERAENDERT: es zu
 * leeren hiesse „keine Zeile ist eine Buchung", und das ist eine Aussage, die
 * niemand getroffen hat. Es gilt dann weiter der Praefix- und Textrueckfall,
 * also genau der Stand vor S5b.
 *
 * Sonst ERSETZT die Ladung den frueheren Stand desselben Projekts: gelesen
 * wurde der ganze Stand und nicht ein Zuwachs. Zurueckgenommene Zeilen sind
 * mitgelesen (`voided_at` steht in der Auswahl); das Register laesst sie aus.
 *
 * `nurWennNochRegistriert` ist fuer die NACHLADUNG eines Schreibpfades: bis
 * die Zeilen da sind, kann das Projekt geschlossen oder die Sitzung beendet
 * sein. Dann darf die Antwort keinen Stand mehr anlegen — er gehoerte
 * niemandem und bliebe bis zur Abmeldung liegen. Die erste Ladung beim
 * Oeffnen setzt die Marke NICHT: sie ist es ja gerade, die den Stand anlegt.
 *
 * ─── DIE JUENGSTE LADUNG GEWINNT, AELTERE ANTWORTEN WERDEN VERWORFEN ─────
 * Zwei Ladungen desselben Projekts koennen in BELIEBIGER Reihenfolge
 * zurueckkommen. Weil jede den ganzen Stand ERSETZT, wuerde eine spaet
 * eintreffende alte Antwort die neue ueberschreiben — und weil `registriere`
 * ersetzt und nicht ergaenzt, waere die frisch angehaengte Zeile danach weg
 * (Nachladung nach dem Anhaengen kommt an, die zuvor gestartete
 * Oeffnen-Ladung landet danach). Dasselbe bei A → B → A: die Wache in App.tsx
 * verhindert das VERGESSEN, nicht das Ueberschreiben.
 *
 * Deshalb zieht jede Ladung VOR dem Lesen eine laufende Nummer je Projekt.
 * Trifft sie zurueck und ist die Nummer nicht mehr die juengste, laesst sie
 * das Register in Ruhe und meldet `null`.
 */
const ladegeneration = new Map<string, number>();

/** Nur fuer Tests: die laufenden Nummern zuruecksetzen. */
export const resetLadegeneration = (): void => {
    ladegeneration.clear();
};

export const ladeBuchungsartenInsRegister = async (
    projectId: string,
    optionen?: { nurWennNochRegistriert?: boolean },
): Promise<number | null> => {
    const projekt = (projectId || '').trim();
    const meine = (ladegeneration.get(projekt) ?? 0) + 1;
    ladegeneration.set(projekt, meine);
    // DREIZEHN Spalten statt `*`: ein Projekt mit tausenden Journalzeilen zoege
    // sonst bei jedem Oeffnen das ganze Protokoll ein zweites Mal ueber die
    // Leitung, nur um diese Felder daraus zu lesen. Alle stammen aus Nr. 59 —
    // der Client setzt damit keine neue Migration voraus.
    const rows = await journalSeitenweise<BuchungsartZeile>(projectId, REGISTER_SPALTEN);
    if (!rows) return null;
    // Beide Pruefungen fallen NACH dem Lesen, nicht davor: dazwischen liegt
    // das Netz. Erst die juengere Ladung — sie hat den besseren Stand —,
    // dann die Frage, ob das Projekt ueberhaupt noch geladen ist.
    if (ladegeneration.get(projekt) !== meine) return null;
    if (optionen?.nurWennNochRegistriert && !istProjektRegistriert(projectId)) return null;
    return registriereBuchungsarten(projectId, rows);
};

// ═══════════════════════════════════════════════════════════════════════════
// Die gemeinsame Zugriffsfunktion
// ═══════════════════════════════════════════════════════════════════════════

/** Das Wenige, das `getServiceLog` von einem Projekt braucht. */
export interface ProjectLike {
    metadata?: { globalData?: { serviceLog?: ServiceTransaction[] } | null } | null;
}

let journalTreffer = 0;
let jsonRueckfaelle = 0;

/**
 * Wie oft wurde bisher auf das JSON zurueckgefallen — und wie oft nicht?
 *
 * Der Zaehler ist die spaetere ABSCHALTBEDINGUNG der Uebergangsphase (S9):
 * solange er waechst, liest noch jemand am Journal vorbei, und die
 * Doppelschreibphase darf nicht enden. Bewusst nur ein Modul-Zaehler ohne
 * Speicherung — er beantwortet eine Ja/Nein-Frage, keine Statistik.
 */
export const serviceLogAccessStats = (): { journal: number; json: number } =>
    ({ journal: journalTreffer, json: jsonRueckfaelle });

/** Nur fuer Tests und fuer den Beginn eines Beobachtungsfensters. */
export const resetServiceLogAccessStats = (): void => {
    journalTreffer = 0;
    jsonRueckfaelle = 0;
};

/**
 * DAS LEISTUNGSPROTOKOLL EINES PROJEKTS — die eine Stelle, an der entschieden
 * wird, woher es kommt.
 *
 * Bis heute gibt es diese Funktion nicht: rund 65 Stellen greifen selbst nach
 * `metadata.globalData.serviceLog`. Solange das so ist, laesst sich die Quelle
 * nicht umstellen, ohne 65 Stellen anzufassen — und jede von ihnen ist eine
 * Gelegenheit, eine zu vergessen.
 *
 * Vorrang hat das Journal. Es ist nicht „auch da", es ist die belastbarere
 * Quelle: relational, mit den Aussagen als Spalten und ohne die Schreibrennen
 * des JSON-Blobs. Fehlt es (Migration nicht gelaufen, offline, Projekt noch
 * nicht uebernommen), gilt das JSON — unveraendertes Verhalten.
 *
 * Bewusst SYNCHRON und mit dem Journal als Parameter: die Aufrufer sind
 * ueberwiegend Renderpfade und reine Ableitungen, die kein `await` vertragen.
 * Das Laden gehoert eine Ebene hoeher (`loadJournal`, einmal je geoeffnetem
 * Projekt), nicht in jede Auswertung.
 *
 * ─── Ein LEERES Journal ist ein Journal ────────────────────────────────────
 * `null`/`undefined` heisst „nicht geladen" und faellt auf das JSON zurueck;
 * ein leeres Array heisst „geladen, und dieses Projekt hat keine Zeilen" und
 * bleibt leer. Die beiden gleichzusetzen waere ein stiller Datenrueckfall:
 * nach einer vollstaendigen Ruecknahme (`service_journal_void`) stuenden die
 * alten JSON-Zeilen wieder da, und zurueckgenommene Leistungen waeren wieder
 * abrechenbar.
 */
export const getServiceLog = (
    project: ProjectLike | null | undefined,
    journal?: ServiceJournalRow[] | null,
): ServiceTransaction[] => {
    if (journal) {
        journalTreffer++;
        return journalToServiceTransactions(journal);
    }
    jsonRueckfaelle++;
    const log = project?.metadata?.globalData?.serviceLog;
    return Array.isArray(log) ? log : [];
};

// ═══════════════════════════════════════════════════════════════════════════
// Das Doppelschreiben (S4b)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Ein Schreibvorgang, der ausser dem JSON auch das Journal erreichen soll.
 *
 * `entries` sind AUSSCHLIESSLICH die NEUEN Zeilen dieses Vorgangs, nicht das
 * ganze Protokoll: das Journal ist einfuegend (`on conflict do nothing`), und
 * jede bereits uebernommene Zeile traegt denselben inhaltsabgeleiteten
 * Schluessel. Den ganzen Log zu schicken waere zwar folgenlos, aber bei jedem
 * Speichern ein Vielfaches an Nutzlast.
 *
 * `previousLog` ist der Stand VOR diesem Vorgang. Er liefert die bereits
 * vergebenen ids — ohne ihn bekaeme eine neue Zeile mit einer im Projekt schon
 * belegten id keinen Ersatzschluessel, und `on conflict do nothing`
 * verschluckte sie.
 */
export interface JournalMirrorRequest {
    projectId: string;
    entries: ServiceTransaction[];
    previousLog?: ServiceTransaction[] | null;
    source: JournalSource;
    linkedProjectConfig?: LinkedProjectConfig | null;
    statements?: Record<string, JournalEntryStatement>;
}

export interface JournalMirrorResult extends JournalAppendResult {
    /** Wie viele Zeilen an die RPC gingen. */
    rows: number;
    /** Was nicht abbildbar war und deshalb NICHT im Journal steht. */
    skipped: ServiceTransaction[];
}

/** Die im Projekt bereits vergebenen Eintrags-ids. */
const idsOf = (log: ServiceTransaction[] | null | undefined): string[] =>
    (log ?? []).map(e => (typeof e?.id === 'string' ? e.id.trim() : '')).filter(Boolean);

/**
 * Die Zweitschrift eines Schreibvorgangs — JSON zuerst, Journal danach.
 *
 * Wirft nie (`appendToJournal` faengt alles ab). Nicht abbildbare Zeilen
 * werden GEMELDET und nicht verschwiegen: der Pruefsatz der Umstellung lautet,
 * dass keine serviceLog-Zeile am Ende weder im Journal noch in der Quarantaene
 * steht, und im Client ist das Protokoll der einzige Ort, an dem so eine Zeile
 * auffallen kann.
 */
export const mirrorEntriesToJournal = async (
    req: JournalMirrorRequest,
): Promise<JournalMirrorResult> => {
    // Zuerst in den Ausgangskorb, dann an die RPC: der Aufrufer hat das JSON
    // bereits bestaetigt gespeichert, die Zeile ist also FAELLIG. Schliesst
    // jemand die Seite, bevor die RPC antwortet, oder scheitert sie still,
    // holt `retryJournalMirrors` sie beim naechsten Start nach — nach einer
    // Ruecksprache mit dem Server, ob das JSON die Zeile noch traegt.
    const korbId = neueKorbId();
    await korbSchreiben({
        id: korbId,
        projectId: (req.projectId || '').trim(),
        userId: await aktuelleUserId(),
        state: 'faellig',
        request: kopieDerAnfrage(req),
        createdAt: new Date().toISOString(),
        attempts: 0,
        lastError: null,
    });
    return sendeAusKorb(korbId, req);
};

/** Der eigentliche Schreibvorgang — ohne Korb, ohne Vormerkung. */
const spiegelnOhneKorb = async (req: JournalMirrorRequest): Promise<JournalMirrorResult> => {
    const entries = (req.entries ?? []).filter(Boolean);
    const skipped = entries.filter(e => !isJournalMappable(e));
    if (skipped.length > 0) {
        console.warn(
            `[serviceJournal] ${skipped.length} Zeile(n) nicht abbildbar (${req.source}), nicht im Journal:`,
            skipped.map(e => ({ id: e?.id, serviceId: e?.serviceId, date: e?.date, quantity: e?.quantity })),
        );
    }

    const rows = toJournalRows({
        projectId: req.projectId,
        entries,
        source: req.source,
        linkedProjectConfig: req.linkedProjectConfig ?? null,
        statements: req.statements,
        knownIds: idsOf(req.previousLog),
    });
    if (rows.length === 0) return { written: false, reason: 'leer', rows: 0, skipped };

    const ergebnis = await appendToJournal(req.projectId, rows);
    // Die EINZIGE Anhaengestelle des Moduls: `mirrorEntriesToJournal`,
    // `sendeAusKorb` und `vormerkungSchreiben` laufen alle hier durch. Die
    // geschriebenen Zeilen tragen ihre Buchungsart, der JSON-Eintrag daneben
    // nicht — ohne diese Zeile entschiede `isPostingEntry` ueber eine gerade
    // gebuchte Zeile bis zum naechsten Oeffnen wieder nach Beleg und Text.
    //
    // NACHGELADEN und nicht aus `rows` uebernommen: die RPC haengt mit
    // `on conflict do nothing` an. Was der Server VERWORFEN hat — dieselbe id
    // steht dort schon, etwa nach dem Umzug einer Tochterzeile an die Mutter —
    // stuende sonst mit der Art dieses Versuchs im Register, obwohl das
    // Journal etwas anderes traegt. Gefragt wird deshalb der Server.
    //
    // Nur fuer ein Projekt, dessen Stand ueberhaupt geladen ist: ein fremdes
    // Projekt (der Abrechnungsdialog bucht auf Projekte, die niemand geoeffnet
    // hat) bekaeme sonst einen halben Stand, der bis zur Abmeldung liegen
    // bliebe. Dort gilt weiter der Praefix- und Textrueckfall.
    //
    // `nurWennNochRegistriert`: zwischen dem Anhaengen und der Antwort des
    // Servers kann das Projekt geschlossen oder abgemeldet worden sein. Die
    // zweite Pruefung faellt deshalb erst, wenn die Zeilen da sind.
    if (ergebnis.written && istProjektRegistriert(req.projectId)) {
        void ladeBuchungsartenInsRegister(req.projectId, { nurWennNochRegistriert: true })
            .catch(() => null);
    }
    return { ...ergebnis, rows: rows.length, skipped };
};

/**
 * Die Zweitschrift OHNE Ausgangskorb — fuer Aufrufer, die eine ENDGUELTIGE
 * Antwort brauchen, weil ein spaeterer Wiederholungslauf schaden wuerde.
 *
 * Der Fall: die gekuerzte Zeile der teilweisen Ruecknahme
 * (serviceReversalService). Sie ERSETZT eine lebende Journalzeile; erst wenn
 * sie sicher steht, wird die alte stillgelegt. Laege sie im Korb und kaeme
 * Stunden spaeter nach, stuende sie NEBEN der alten, die dann niemand mehr
 * stilllegt — zwei lebende Mengen fuer eine Zeile. Scheitert das Anhaengen
 * hier, bleibt die alte Zeile allein stehen, die Abweichungssicht meldet sie
 * (`wert_weicht_ab`), und die naechste Ruecknahme desselben Eintrags legt sie
 * mit still.
 *
 * Seit SQL Nr. 136 ist das nur noch der RUECKFALL: ersetzt wird ueber
 * `ersetzeJournalzeilen` (stilllegen, dann einfuegen, in einer Transaktion).
 * Dieser Weg laeuft nur noch, wenn die Funktion fehlt (PGRST202) oder nichts
 * Lebendes zu ersetzen ist. Bei einer Berichtszeile scheitert er am
 * Eindeutigkeitsriegel `service_journal_bericht_leistung_uq` (23505) — genau
 * der Befund, wegen dessen es Nr. 136 gibt.
 */
export const mirrorEntriesToJournalOhneKorb = (req: JournalMirrorRequest): Promise<JournalMirrorResult> =>
    spiegelnOhneKorb(req);

// ═══════════════════════════════════════════════════════════════════════════
// Der Ausgangskorb — Vormerkungen und faellige Zweitschriften ueberleben die
// Sitzung (journalMirrorOutbox.ts; die Begruendung steht dort im Kopf)
// ═══════════════════════════════════════════════════════════════════════════

const neueKorbId = (): string => {
    const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    return c?.randomUUID ? c.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const kopieDerAnfrage = (req: JournalMirrorRequest): JournalMirrorRequest => ({
    ...req,
    projectId: (req.projectId || '').trim(),
    entries: [...(req.entries ?? []).filter(Boolean)],
    previousLog: [...(req.previousLog ?? [])],
});

/** Die angemeldete Person — `null`, wenn die Sitzung nicht lesbar ist (Tests, Abmeldung). */
const aktuelleUserId = async (): Promise<string | null> => {
    try {
        const auth = (supabase as { auth?: { getSession?: () => Promise<{ data?: { session?: { user?: { id?: string } } | null } }> } }).auth;
        if (!auth?.getSession) return null;
        const { data } = await auth.getSession();
        return data?.session?.user?.id ?? null;
    } catch {
        return null;
    }
};

// Kein Fehler des Korbs darf einen Speichervorgang oder Versand beeinflussen:
// jede der drei Helfer schluckt und meldet nur im Protokoll.
const korbSchreiben = async (eintrag: JournalMirrorOutboxEntry): Promise<void> => {
    const korb = journalMirrorOutboxStore();
    if (!korb || !eintrag.projectId) return;
    try { await korb.put(eintrag); } catch (e) { console.warn('[serviceJournal] Ausgangskorb nicht beschreibbar:', e); }
};

const korbLoeschen = async (id: string): Promise<void> => {
    const korb = journalMirrorOutboxStore();
    if (!korb) return;
    try { await korb.delete(id); } catch (e) { console.warn('[serviceJournal] Ausgangskorb nicht bereinigbar:', e); }
};

const korbFehlschlagVermerken = async (id: string, error: unknown): Promise<void> => {
    const korb = journalMirrorOutboxStore();
    if (!korb) return;
    try {
        const alt = await korb.get(id);
        if (!alt) return;
        const text = (error as { message?: string })?.message ?? (error ? String(error) : null);
        await korb.put({ ...alt, state: 'faellig', attempts: alt.attempts + 1, lastError: text ? text.slice(0, 300) : null });
    } catch (e) {
        console.warn('[serviceJournal] Ausgangskorb nicht fortschreibbar:', e);
    }
};

/**
 * Schreiben und den Korb danach aufraeumen. Bleibt liegen (als `faellig`),
 * was mit einem Fehler endete; `rpc_fehlt` ist der geplante Zustand zwischen
 * Client-Deploy und SQL-Lauf und wird NICHT aufgehoben — sonst sammelte TEST
 * ohne die RPC endlos Eintraege.
 */
const sendeAusKorb = async (korbId: string, req: JournalMirrorRequest): Promise<JournalMirrorResult> => {
    const ergebnis = await spiegelnOhneKorb(req);
    if (ergebnis.written || ergebnis.reason === 'leer' || ergebnis.reason === 'rpc_fehlt') await korbLoeschen(korbId);
    else await korbFehlschlagVermerken(korbId, ergebnis.error);
    return ergebnis;
};

/**
 * Eine Anfrage am mitgegebenen Protokoll teilen: `drin` steht im Protokoll und
 * darf geschrieben werden, `draussen` (noch) nicht.
 *
 * ─── Warum `draussen` NICHT verworfen wird (09.09.2026) ─────────────────────
 * Der Versanddialog merkt seine Zweitschrift VOR dem Speichern vor. Dauert das
 * Speichern lange (Fotos: 26051007 #32, 72 s zwischen Mail und Buchung), feuert
 * derweil der Autosave-Flush DESSELBEN Projekts, nimmt die Vormerkung mit und
 * filtert sie gegen den Stand der Warteschlange — der die Buchungszeile noch
 * nicht kennt. Bis zum 08.09. hiess das „leer" → Korbeintrag geloescht → die
 * Zeile fehlte im Journal bis zum Nachtlauf (26051007 #32, 6204 #21,
 * 26031013 #19, 26091007 #1). Ein Flush sagt nur, was sein Stand traegt; ob
 * eine Zeile NIE kommt, weiss allein der Server — und den fragt
 * `retryJournalMirrors`. Deshalb bleibt `draussen` vorgemerkt.
 */
const teileAmJson = (
    req: JournalMirrorRequest,
    log: ServiceTransaction[] | null | undefined,
): { drin: JournalMirrorRequest; draussen: JournalMirrorRequest } => {
    if (!log) return { drin: req, draussen: { ...req, entries: [] } };
    const ids = new Set(idsOf(log));
    const drin = req.entries.filter(e => typeof e?.id === 'string' && ids.has(e.id.trim()));
    const draussen = req.entries.filter(e => !(typeof e?.id === 'string' && ids.has(e.id.trim())));
    return { drin: { ...req, entries: drin }, draussen: { ...req, entries: draussen } };
};

/**
 * Den Korbeintrag auf den Rest zurueckschreiben — als `vorgemerkt`, damit
 * der naechste Flush oder der Wiederholungslauf ihn wieder findet.
 */
const korbAufRestSetzen = async (korbId: string, rest: JournalMirrorRequest): Promise<void> => {
    const korb = journalMirrorOutboxStore();
    if (!korb) return;
    try {
        const alt = await korb.get(korbId);
        if (!alt) return;
        await korb.put({ ...alt, state: 'vorgemerkt', request: rest });
    } catch (e) {
        console.warn('[serviceJournal] Ausgangskorb nicht fortschreibbar:', e);
    }
};

/**
 * Eine Vormerkung gegen das geschriebene Protokoll schreiben: was drin ist,
 * geht an die RPC; was draussen ist, bleibt vorgemerkt (Korb UND Speicher).
 */
const vormerkungSchreiben = async (
    korbId: string,
    req: JournalMirrorRequest,
    savedLog: ServiceTransaction[] | null | undefined,
    liegenlassen: (rest: Vormerkung) => void,
): Promise<JournalMirrorResult | null> => {
    const { drin, draussen } = teileAmJson(req, savedLog);
    let ergebnis: JournalMirrorResult | null = null;
    if (drin.entries.length > 0) {
        if (draussen.entries.length === 0) {
            ergebnis = await sendeAusKorb(korbId, drin);
        } else {
            ergebnis = await spiegelnOhneKorb(drin);
            if (!ergebnis.written && ergebnis.reason === 'fehler') {
                // Der geschriebene Teil ist gescheitert: ganze Anfrage bleibt
                // faellig, der Wiederholungslauf prueft sie gegen den Server.
                await korbFehlschlagVermerken(korbId, ergebnis.error);
                liegenlassen({ korbId, req, abgelegt: Promise.resolve() });
                return ergebnis;
            }
        }
    }
    if (draussen.entries.length > 0) {
        const abgelegt = korbAufRestSetzen(korbId, draussen);
        liegenlassen({ korbId, req: draussen, abgelegt });
    }
    return ergebnis;
};

/**
 * Das JSON fuehrt: aus einer Anfrage bleibt nur, was das mitgegebene Protokoll
 * heute traegt. Ohne Protokoll (`null`) bleibt alles — der Aufrufer hat dann
 * keinen Stand, gegen den er pruefen koennte, und die Anfrage stammt aus
 * einem bestaetigten Speichern.
 */
/**
 * Nur die Zeilen der Anfrage, die das Server-JSON (noch) traegt — und zwar
 * mit DERSELBEN Menge.
 *
 * Die Menge zaehlt, seit die teilweise Ruecknahme eine bestehende Zeile in
 * place kuerzt (serviceReversalService): scheitert das Anhaengen der
 * gekuerzten Zeile (4 → 3) und wird sie danach ein zweites Mal gekuerzt
 * (3 → 2), laege im Korb noch die 3. Nach id allein wuerde der
 * Wiederholungslauf sie anhaengen — mit eigenem, aus dem Inhalt abgeleitetem
 * Schluessel neben der 2, und das Journal fuehrte fuer eine Zeile zwei
 * lebende Mengen. Eine Korbzeile, deren Menge der Server nicht mehr kennt,
 * ist ueberholt und wird verworfen. Traegt der Serverstand keine Menge
 * (nur ids geladen), entscheidet die id wie bisher.
 *
 * Ebenso die POSITION (Review 02.10.2026): der Zuordnungsumzug
 * (`moveAltBackfill`) haengt eine Zeile in place auf eine andere Position, die
 * id bleibt, oft auch die Menge. Laege von dieser Zeile noch eine Zweitschrift
 * im Korb, haengte der Wiederholungslauf sie nach id und Menge an — auf der
 * ALTEN Position, mit eigenem, aus dem Inhalt abgeleitetem Schluessel neben
 * der Zeile, die der Umzug gerade ersetzt hat. Eine Korbzeile auf einer
 * Position, die der Server fuer diese id nicht mehr fuehrt, ist ueberholt.
 * Fehlt die Position auf einer Seite, entscheidet die id wie bei der Menge.
 */
const nurWasDasJsonTraegt = (
    req: JournalMirrorRequest,
    log: ReadonlyArray<ServerLogZeile> | null | undefined,
): JournalMirrorRequest => {
    if (!log) return req;
    // Leer heisst „nicht bekannt" — nie „keine Position".
    const positionVon = (wert: unknown): string => (typeof wert === 'string' ? wert.trim() : '');
    // ALLE Serverzeilen je id (Codex-Review 02.10.2026, P2): steht eine id
    // doppelt im Protokoll (`doppelt_im_projekt`) — etwa auf zwei Positionen
    // —, ueberschriebe eine Map mit EINEM Eintrag die erste Zeile durch die
    // letzte, und eine gueltige Korbzeile der ersten fiele weg. Behalten wird
    // die Korbzeile, wenn IRGENDEINE Serverzeile mit dieser id passt.
    const amServer = new Map<string, Array<{ menge: number | null | undefined; position: string }>>();
    for (const e of log) {
        const id = typeof e?.id === 'string' ? e.id.trim() : '';
        if (!id) continue;
        const zeile = {
            menge: 'quantity' in (e ?? {}) ? journalQuantity(e.quantity) : undefined,
            position: positionVon(e?.serviceId),
        };
        const liste = amServer.get(id);
        if (liste) liste.push(zeile);
        else amServer.set(id, [zeile]);
    }
    return {
        ...req,
        entries: req.entries.filter(e => {
            const id = typeof e?.id === 'string' ? e.id.trim() : '';
            const zeilen = id ? amServer.get(id) : undefined;
            if (!zeilen) return false;
            const eigene = journalQuantity(e.quantity);
            const eigenePosition = positionVon(e?.serviceId);
            return zeilen.some(server => {
                // Verglichen werden nur zwei ZAHLEN. Fehlt eine Seite
                // (Serverzeile ohne Menge, Korbzeile ohne Menge), entscheidet
                // die id wie bisher — eine Zeile ohne Menge ist ohnehin nicht
                // abbildbar.
                const mengePasst = server.menge === undefined || server.menge === null || eigene === null
                    || Math.abs(server.menge - eigene) < 0.0005;
                // Dieselbe Regel fuer die Position: nur zwei bekannte Werte
                // koennen sich widersprechen.
                const positionPasst = !server.position || !eigenePosition || server.position === eigenePosition;
                return mengePasst && positionPasst;
            });
        }),
    };
};

interface Vormerkung {
    korbId: string;
    req: JournalMirrorRequest;
    /** Der Korbeintrag ist geschrieben (oder es gibt keinen Korb). */
    abgelegt: Promise<void>;
}

/**
 * Vorgemerkte Zweitschriften je Projekt — fuer die Schreibpfade, die NICHT
 * selbst speichern.
 *
 * Die Modul- und Berichtspfade rufen `onUpdate(...)`; geschrieben wird von dort
 * aus erst zwei Sekunden spaeter ueber die Offline-Queue, und ob das gelingt,
 * weiss die Aufrufstelle nicht. Wuerde sie sofort anhaengen, stuende bei einem
 * gescheiterten Speichern eine Zeile im Journal, die das JSON nie bekommen hat
 * — und die Reparaturrichtung der Uebergangsphase ist ausdruecklich nur
 * JSON -> Journal, weil das Journal weder Update noch Delete kennt.
 *
 * Deshalb: vormerken, und erst nach dem BESTAETIGTEN Flush anhaengen
 * (`flushJournalMirror`, gerufen aus dem `onFlushed` der Queue). Die
 * Vormerkung liegt seit dem 07.09.2026 zusaetzlich im Ausgangskorb
 * (IndexedDB): faellt die Sitzung weg, schiebt die Warteschlange den Stand
 * spaeter trotzdem hoch, und `retryJournalMirrors` holt die Zweitschrift dann
 * nach — nach Ruecksprache mit dem Server, ob das JSON die Zeile traegt.
 */
const vorgemerkt = new Map<string, Vormerkung[]>();

/** Eine Zweitschrift vormerken; geschrieben wird beim naechsten Flush. */
export const queueJournalMirror = (req: JournalMirrorRequest): void => {
    const id = (req.projectId || '').trim();
    const entries = (req.entries ?? []).filter(Boolean);
    if (!id || entries.length === 0) return;
    const kopie = kopieDerAnfrage(req);
    const korbId = neueKorbId();
    const createdAt = new Date().toISOString();
    const abgelegt = aktuelleUserId().then(userId => korbSchreiben({
        id: korbId, projectId: id, userId, state: 'vorgemerkt', request: kopie, createdAt, attempts: 0, lastError: null,
    }));
    const liste = vorgemerkt.get(id) ?? [];
    liste.push({ korbId, req: kopie, abgelegt });
    vorgemerkt.set(id, liste);
};

/**
 * Die Vormerkungen eines Projekts schreiben — nach erfolgreichem Speichern.
 *
 * `savedLog` ist das Protokoll, das der Flush WIRKLICH geschrieben hat (nach
 * einer Zusammenfuehrung nicht dasselbe wie der eingereihte Stand). Ist es
 * mitgegeben, geht nur ins Journal, was darin steht.
 *
 * Nach den Vormerkungen dieser Sitzung kommen die aus dem Korb: Eintraege
 * frueherer Sitzungen zu diesem Projekt, deren Flush erst jetzt gelungen ist.
 */
export const flushJournalMirror = async (
    projectId: string,
    savedLog?: ServiceTransaction[] | null,
): Promise<JournalMirrorResult[]> => {
    const id = (projectId || '').trim();
    if (!id) return [];
    // Vor dem ersten `await` entnehmen: sonst schriebe ein zweiter Flush
    // dieselben Zeilen ein zweites Mal an die RPC.
    const liste = vorgemerkt.get(id) ?? [];
    vorgemerkt.delete(id);
    const ergebnisse: JournalMirrorResult[] = [];
    const gesehen = new Set<string>();
    // Was dieser Flush nicht schreiben darf, kommt hier wieder hinein — ein
    // Flush entscheidet nur ueber seinen eigenen Stand, nie ueber die Zukunft.
    const bleibt: Vormerkung[] = [];
    const liegenlassen = (v: Vormerkung) => { bleibt.push(v); };
    for (const v of liste) {
        await v.abgelegt.catch(() => undefined);
        gesehen.add(v.korbId);
        const ergebnis = await vormerkungSchreiben(v.korbId, v.req, savedLog, liegenlassen);
        if (ergebnis) ergebnisse.push(ergebnis);
    }

    const korb = journalMirrorOutboxStore();
    if (korb) {
        let rest: JournalMirrorOutboxEntry[] = [];
        try {
            rest = (await korb.listByProject(id)).filter(e => e.state === 'vorgemerkt' && !gesehen.has(e.id));
        } catch (e) {
            console.warn('[serviceJournal] Ausgangskorb nicht lesbar:', e);
        }
        // Korbeintraege frueherer Sitzungen: Rest bleibt im Korb (der
        // Wiederholungslauf prueft ihn gegen den Server), nicht im Speicher.
        for (const e of rest) {
            const ergebnis = await vormerkungSchreiben(e.id, e.request, savedLog, () => undefined);
            if (ergebnis) ergebnisse.push(ergebnis);
        }
    }
    if (bleibt.length > 0) vorgemerkt.set(id, [...bleibt, ...(vorgemerkt.get(id) ?? [])]);
    return ergebnisse;
};

/** Wie viele Vormerkungen liegen noch — fuer Tests und Diagnose. */
export const pendingJournalMirrors = (projectId?: string): number => {
    if (projectId) return (vorgemerkt.get(projectId.trim()) ?? []).length;
    let n = 0;
    for (const liste of vorgemerkt.values()) n += liste.length;
    return n;
};

/** Nur fuer Tests: alle Vormerkungen verwerfen. */
export const resetJournalMirrorQueue = (): void => { vorgemerkt.clear(); };

/** Was der Wiederholungslauf zum Pruefen braucht — austauschbar fuer Tests. */
/** Was der Wiederholungslauf vom Server-Protokoll braucht: id, Menge und Position. */
export type ServerLogZeile = Pick<ServiceTransaction, 'id'> & Partial<Pick<ServiceTransaction, 'quantity' | 'serviceId'>>;

export interface RetryJournalMirrorsDeps {
    /** Liegt fuer dieses Projekt noch ein Eintrag in der Offline-Warteschlange? */
    hasPendingQueueEntry: (userId: string, projectId: string) => Promise<boolean>;
    /**
     * Das Protokoll auf dem Server — id, Menge UND Position je Zeile; `null` =
     * nicht lesbar (spaeter noch einmal). Menge und Position braucht
     * `nurWasDasJsonTraegt`, um eine ueberholte Korbzeile zu erkennen
     * (Begruendung dort).
     */
    loadServerLog: (projectId: string) => Promise<ServerLogZeile[] | null>;
    /**
     * Nur die ids — der aeltere Vertrag, den Tests und Aufrufer weiter
     * bedienen duerfen. Ist er gesetzt, ersetzt er `loadServerLog`; die
     * Pruefung von Menge und Position entfaellt dann (ids allein sagen nichts
     * darueber).
     */
    loadServerLogIds?: (projectId: string) => Promise<string[] | null>;
    now?: () => number;
}

const standardRetryDeps: RetryJournalMirrorsDeps = {
    hasPendingQueueEntry: async (userId, projectId) => {
        try { return !!(await getQueueEntry(userId, projectId)); } catch { return false; }
    },
    loadServerLog: async projectId => {
        try {
            const { data, error } = await supabase
                .from('projects')
                .select('log:data->metadata->globalData->serviceLog')
                .eq('id', projectId)
                .maybeSingle();
            if (error) return null;
            if (!data) return [];
            const log = (data as { log?: unknown }).log;
            if (!Array.isArray(log)) return [];
            return (log as ServiceTransaction[])
                .filter(e => typeof e?.id === 'string' && e.id.trim())
                .map(e => ({ id: e.id.trim(), quantity: e.quantity, serviceId: e.serviceId }));
        } catch {
            return null;
        }
    },
};

export interface RetryJournalMirrorsResult {
    geprueft: number;
    geschrieben: number;
    verworfen: number;
    /** Warten weiter — auf den Flush ihres Projekts oder auf den naechsten Lauf. */
    offen: number;
}

/** Aelter als das wird ein Korbeintrag verworfen; die Nachspiegelung Nr. 76 hat ihn laengst gesehen. */
const KORB_HALTEZEIT_MS = 30 * 24 * 60 * 60 * 1000;
/** Juenger als das wird ein faelliger Eintrag nicht angefasst — seine RPC laeuft womoeglich gerade noch. */
const KORB_SCHONFRIST_MS = 60 * 1000;

let retryLaeuft = false;

/**
 * Den Ausgangskorb abarbeiten — beim Start, beim Online-Gehen, beim
 * Sichtbarwerden des Tabs.
 *
 * Fuer jeden Eintrag gilt: das JSON fuehrt. Ein `vorgemerkt`, dessen Projekt
 * noch in der Warteschlange liegt, wartet weiter (sein Flush ruft
 * `flushJournalMirror`). Alles andere wird gegen das Protokoll auf dem Server
 * geprueft: was dort steht, wird angehaengt, was dort fehlt, verworfen —
 * eine Journalzeile ohne JSON-Zeile darf nicht entstehen. Ist der Server nicht
 * lesbar, bleibt der Eintrag fuer den naechsten Lauf liegen.
 */
export const retryJournalMirrors = async (
    userId: string | null,
    deps: Partial<RetryJournalMirrorsDeps> = {},
): Promise<RetryJournalMirrorsResult> => {
    const bilanz: RetryJournalMirrorsResult = { geprueft: 0, geschrieben: 0, verworfen: 0, offen: 0 };
    const korb = journalMirrorOutboxStore();
    if (!korb || retryLaeuft) return bilanz;
    const d: RetryJournalMirrorsDeps = { ...standardRetryDeps, ...deps };
    const jetzt = (d.now ?? Date.now)();
    retryLaeuft = true;
    try {
        let eintraege: JournalMirrorOutboxEntry[] = [];
        try { eintraege = await korb.listAll(); } catch (e) {
            console.warn('[serviceJournal] Ausgangskorb nicht lesbar:', e);
            return bilanz;
        }
        for (const e of eintraege) {
            // Fremde Eintraege (Kontowechsel auf demselben Geraet) bleiben liegen.
            if (e.userId && userId && e.userId !== userId) { bilanz.offen += 1; continue; }
            bilanz.geprueft += 1;
            const alter = jetzt - Date.parse(e.createdAt);
            if (Number.isFinite(alter) && alter > KORB_HALTEZEIT_MS) { await korbLoeschen(e.id); bilanz.verworfen += 1; continue; }
            if (e.state === 'faellig' && Number.isFinite(alter) && alter < KORB_SCHONFRIST_MS) { bilanz.offen += 1; continue; }
            if (e.state === 'vorgemerkt') {
                const wer = e.userId ?? userId;
                if (!wer) { bilanz.offen += 1; continue; }
                if (await d.hasPendingQueueEntry(wer, e.projectId)) { bilanz.offen += 1; continue; }
            }
            const serverLog: ServerLogZeile[] | null = d.loadServerLogIds
                ? await d.loadServerLogIds(e.projectId).then(ids => (ids === null ? null : ids.map(id => ({ id }))))
                : await d.loadServerLog(e.projectId);
            if (serverLog === null) { bilanz.offen += 1; continue; }
            const req = nurWasDasJsonTraegt(e.request, serverLog);
            if (req.entries.length === 0) { await korbLoeschen(e.id); bilanz.verworfen += 1; continue; }
            const ergebnis = await sendeAusKorb(e.id, req);
            if (ergebnis.written) bilanz.geschrieben += 1;
            else if (ergebnis.reason === 'fehler') bilanz.offen += 1;
            else bilanz.verworfen += 1;
        }
    } finally {
        retryLaeuft = false;
    }
    return bilanz;
};

// ═══════════════════════════════════════════════════════════════════════════
// Die Ruecknahme (S4c)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Warum eine Journalzeile zurueckgenommen wird — die vier Gruende, die
 * `service_journal_void` (Nr. 61) annimmt. Jeder andere Wert laesst die
 * Funktion mit 22023 scheitern.
 */
export type JournalVoidReason = 'issue_failed' | 'storno' | 'backfill_revert' | 'correction';

export interface JournalVoidResult {
    /** Wie viele Zeilen NEU stillgelegt wurden (ein zweiter Aufruf: 0). */
    voided: number;
    /** Zeilen, zu denen es keine Journalzeile gab — nie gespiegelt. */
    unbekannt: string[];
    /**
     * Konnte das Journal ueberhaupt gelesen werden? `false` heisst: Tabelle
     * fehlt, kein Netz oder keine Berechtigung — dann steht in `unbekannt`
     * ALLES, ohne dass darueber etwas gesagt waere. Wer nach der Stilllegung
     * eine Ersatzzeile anhaengen will (teilweise Ruecknahme), darf das nur bei
     * `true`: sonst lebt die alte Zeile womoeglich weiter, und die neue
     * stuende als zweite daneben.
     */
    journalGelesen: boolean;
    error?: unknown;
}

/**
 * Eine Kennung fuer den Vergleich: beschnitten; eine uuid zusaetzlich
 * kleingeschrieben, Alt-Kennungen exakt. Die Regel und ihre Begruendung stehen
 * in serviceLogUtils (`kennungFuerVergleich`): dort erreicht sie auch die
 * Zuordnung (allocationLogic), ohne den Supabase-Client mitzuziehen. Hier nur
 * weitergereicht — Journal und Zuordnung duerfen nie zwei Fassungen derselben
 * Regel fuehren, sonst faende das eine die Zeile und das andere nicht.
 *
 * Der Import steht bewusst HIER und nicht im Kopf (ES-Importe werden ohnehin
 * gehoben): die Aenderung bleibt an der Stelle, an der die Regel frueher
 * definiert war.
 */
import { kennungFuerVergleich } from './serviceLogUtils';
export { kennungFuerVergleich };

/**
 * Die LEBENDEN Zeilen eines schon geladenen Journals, die auf eine JSON-id
 * antworten — ueber `id` oder `legacy_entry_id`. Rein: kein Netz, damit ein
 * Aufrufer mit vielen ids (der Zuordnungsumzug) das Journal EINMAL laedt und
 * hier je id filtert, statt es je id seitenweise neu zu holen.
 *
 * Verglichen wird beschnitten und KLEINGESCHRIEBEN, auf beiden Seiten
 * (Review 02.10.2026): `journalIdDecision` behaelt eine freie uuid aus dem
 * JSON so, wie sie dort steht — `IST_UUID` laesst A–F zu —, die uuid-Spalte
 * liefert sie aber kleingeschrieben zurueck. Ein exakter Vergleich fand die
 * Zeile dann nicht: das Ersetzen sah „nie gespiegelt" und haengte eine zweite
 * lebende Zeile neben die alte (`mehrfach_lebend`). Alt-Kennungen (keine
 * uuid) bleiben exakt — siehe `kennungFuerVergleich`.
 */
export const lebendeZeilenZu = (
    journal: ReadonlyArray<ServiceJournalRow> | null | undefined,
    jsonId: string | null | undefined,
): ServiceJournalRow[] => {
    const gesucht = kennungFuerVergleich(jsonId);
    if (!gesucht) return [];
    return (journal ?? []).filter(row => !!row && !row.voided_at && (
        kennungFuerVergleich(row.id) === gesucht || kennungFuerVergleich(row.legacy_entry_id) === gesucht
    ));
};

/**
 * Die LEBENDEN Journalzeilen, die auf eine JSON-id antworten — ueber `id`
 * oder `legacy_entry_id` (Vergleich siehe `lebendeZeilenZu`). Nach einer
 * teilweisen Ruecknahme ist das die gekuerzte Zeile (abgeleiteter Schluessel,
 * legacy = JSON-id); nach einem frueheren Fehlschlag koennen es zwei sein
 * (alte UND gekuerzte). Wer die Zeile ersetzt, legt ALLE still, nicht nur
 * eine.
 *
 * Liest das Journal jedes Mal FRISCH — das Nachlesen nach einem Anhaengen
 * braucht genau das. Wer fuer viele ids nur den Stand VOR dem Schreiben
 * braucht, laedt einmal (`loadJournal`) und filtert mit `lebendeZeilenZu`.
 *
 * `journalGelesen === false`: Journal nicht lesbar (Tabelle fehlt, kein Netz,
 * keine Berechtigung) — dann ist `zeilen` leer, ohne dass das etwas sagte.
 */
export const ladeLebendeJournalzeilenZu = async (
    projectId: string,
    jsonId: string,
): Promise<{ zeilen: ServiceJournalRow[]; journalGelesen: boolean }> => {
    const id = (projectId || '').trim();
    const gesucht = String(jsonId ?? '').trim();
    if (!id || !gesucht) return { zeilen: [], journalGelesen: true };
    const journal = await loadJournal(id);
    if (!journal) return { zeilen: [], journalGelesen: false };
    return { zeilen: lebendeZeilenZu(journal, gesucht), journalGelesen: true };
};

/**
 * JSON-Zeilen im Journal ZURUECKNEHMEN, nachdem sie aus dem Protokoll
 * entfernt wurden.
 *
 * ─── Warum es diesen Weg ueberhaupt gibt ───────────────────────────────────
 * Das Journal kennt weder update noch delete — auch nicht fuer `service_role`.
 * Ein Protokoll, aus dem sich Zeilen entfernen lassen, ist als Nachweis
 * wertlos. Wo ein Pfad eine JSON-Zeile trotzdem wegnimmt (Storno der
 * Verbuchung, Ruecknahme eines Alt-Nachtrags, Ruecknahme einer Buchung),
 * bliebe die gespiegelte Zeile sonst fuer immer stehen und die
 * Abweichungssicht meldete sie fuer immer mit.
 *
 * `service_journal_void` loescht deshalb nicht, sondern setzt `voided_at`,
 * `voided_by_id` und `void_reason`. Die Zeile bleibt lesbar und sagt, dass
 * sie zurueckgenommen wurde — `journalToServiceTransactions` filtert sie beim
 * Lesen heraus.
 *
 * ─── Die id aus dem JSON ist NICHT immer die id im Journal ─────────────────
 * Eine Zeile, deren JSON-id fehlte, keine uuid war oder im Projekt schon
 * belegt war, traegt im Journal einen abgeleiteten Schluessel; der Rohwert
 * steht in `legacy_entry_id`. Wer blind die JSON-id schickt, trifft diese
 * Zeilen nicht — und weil die Funktion nach ALLES ODER NICHTS arbeitet,
 * scheitert dann der ganze Aufruf und es wird gar nichts zurueckgenommen.
 * Deshalb wird hier zuerst das Journal gelesen und ueber BEIDE Spalten
 * aufgeloest.
 *
 * ─── Was NICHT mitgeschickt wird ───────────────────────────────────────────
 * Zeilen ohne Journalzeile (nie gespiegelt, etwa aus der Zeit vor S4b) und
 * bereits zurueckgenommene. Beides waere kein Fehler des Aufrufers, wuerde
 * aber denselben Alles-oder-nichts-Riegel ausloesen und die uebrigen
 * Ruecknahmen mit sich reissen. Sie werden gemeldet, nicht verschwiegen.
 *
 * ─── Ausfallrichtung ───────────────────────────────────────────────────────
 * Wirft nie. Wie beim Anhaengen ist das JSON zu diesem Zeitpunkt bereits
 * geschrieben; ein Wurf hier zeigte einen Fehler zu einem Vorgang, der
 * gelungen ist. Bleibt eine Zeile stehen, meldet die Abweichungssicht sie —
 * sichtbar. Eine faelschlich zurueckgenommene Zeile waere dagegen still, und
 * das ist die schlechtere Richtung.
 *
 * ─── Das Rennen zwischen Lesen und Zuruecknehmen ist unkritisch ────────────
 * Zwischen dem Lesen des Journals und dem Aufruf kann eine zweite Sitzung
 * dieselbe Zeile zurueckgenommen haben. Das kostet hier nichts: Nr. 61 zaehlt
 * fuer den Alles-oder-nichts-Riegel die Zeilen, die es GIBT und die der
 * Aufrufer sehen darf — `voided_at` spielt dabei keine Rolle. Erst das
 * `update` schliesst mit `voided_at is null` die schon stillgelegten aus. Eine
 * inzwischen zurueckgenommene id laesst den Aufruf also nicht scheitern, sie
 * zaehlt nur nicht mehr mit; die erste Ruecknahme behaelt ihren Zeitpunkt,
 * ihre Person und ihren Grund.
 */
export const voidJournalEntries = async (
    projectId: string,
    entryIds: Array<string | null | undefined>,
    reason: JournalVoidReason,
    opts?: {
        /**
         * Die uebergebenen Werte sind JOURNAL-ids (aus
         * `ladeLebendeJournalzeilenZu`), keine JSON-ids: die Aufloesung ueber
         * `legacy_entry_id` darf dann NICHT greifen. Nach der teilweisen
         * Ruecknahme antwortet die JSON-id auf die neue, lebende Zeile — und
         * genau die soll stehen bleiben, waehrend die alte stillgelegt wird.
         */
        nurJournalIds?: boolean;
    },
): Promise<JournalVoidResult> => {
    const id = (projectId || '').trim();
    const gesucht = (entryIds ?? [])
        .map(v => String(v ?? '').trim())
        .filter(Boolean);
    if (!id || gesucht.length === 0) return { voided: 0, unbekannt: [], journalGelesen: true };

    const journal = await loadJournal(id);
    // `null` heisst „kein Journal bekannt" (Tabelle fehlt, offline, keine
    // Berechtigung) — dann gibt es auch nichts zurueckzunehmen. Das ist der
    // erwartete Zustand vor dem SQL-Lauf und kein Vorfall.
    if (!journal) return { voided: 0, unbekannt: gesucht, journalGelesen: false };

    // Antworten mehrere Zeilen auf denselben Schluessel, gilt eine feste
    // Rangfolge — nie die Ladereihenfolge:
    //   0  exakte id, lebend
    //   1  legacy_entry_id, lebend
    //   2  exakte id, stillgelegt
    //   3  legacy_entry_id, stillgelegt
    //
    // LEBEND VOR STILLGELEGT: die teilweise Ruecknahme (serviceReversalService)
    // legt die gespiegelte Zeile still und haengt die gekuerzte unter
    // abgeleitetem Schluessel mit `legacy_entry_id` = JSON-id an. Wer die
    // Menge ein zweites Mal kuerzt, muss die lebende treffen — traefe er die
    // stillgelegte, geschaehe im Journal nichts, und die naechste gekuerzte
    // Zeile stuende als dritte daneben.
    //
    // UNTER LEBENDEN DIE EXAKTE id: Vorfall 5633 (29.09.2026). Nach
    // moveAltBackfill spiegelte AdminAccountingView die umgezogene ALT-Zeile
    // als neue Journalzeile (legacy_entry_id = alte id) und legte danach die
    // ALTE per id still. Beide lebten; entschieden hat die Ladereihenfolge —
    // getroffen wurde die NEUE (150 ms nach Anlage storniert), die alte blieb
    // lebend auf der Quellposition. Die id gehoert der Zeile, die sie traegt;
    // die legacy-Zeile ist nur ihr Verweis (Primaerschluessel vor Alt-id wie
    // im Buchungsart-Register, serviceLogUtils.aufloesen). Gleicher Rang:
    // die zuerst geladene bleibt, wie bisher.
    //
    // Geschluesselt wird wie in `lebendeZeilenZu` — beschnitten, eine uuid
    // KLEINGESCHRIEBEN (Review 02.10.2026): sie steht im JSON womoeglich in
    // Grossschrift, die uuid-Spalte liefert sie klein zurueck. Exakt
    // verglichen landete sie in `unbekannt` und wurde nicht zurueckgenommen.
    // Alt-Kennungen bleiben exakt (`kennungFuerVergleich`).
    const nachId = new Map<string, { row: ServiceJournalRow; rang: number }>();
    const merke = (schluessel: string, row: ServiceJournalRow, exakt: boolean) => {
        const k = kennungFuerVergleich(schluessel);
        if (!k) return;
        const rang = (row.voided_at ? 2 : 0) + (exakt ? 0 : 1);
        const bisher = nachId.get(k);
        if (!bisher || rang < bisher.rang) nachId.set(k, { row, rang });
    };
    for (const row of journal) {
        merke(row.id, row, true);
        if (row.legacy_entry_id && !opts?.nurJournalIds) merke(row.legacy_entry_id, row, false);
    }

    const ids: string[] = [];
    const unbekannt: string[] = [];
    // Beide Schreibweisen jeder zurueckgenommenen Zeile — das Register kennt
    // die Journal-id UND die urspruengliche JSON-id, der Aufrufer nur eine.
    const vergessen: string[] = [];
    for (const wert of gesucht) {
        const row = nachId.get(kennungFuerVergleich(wert))?.row;
        if (!row) { unbekannt.push(wert); continue; }
        // Schon zurueckgenommen: nicht noch einmal schicken.
        if (row.voided_at) continue;
        if (!ids.includes(row.id)) ids.push(row.id);
        vergessen.push(wert, row.id);
        if (row.legacy_entry_id) vergessen.push(row.legacy_entry_id);
    }

    if (unbekannt.length > 0) {
        console.warn(
            `[serviceJournal] ${unbekannt.length} Zeile(n) ohne Journaleintrag, nicht zurueckgenommen (${reason}):`,
            unbekannt,
        );
    }
    if (ids.length === 0) return { voided: 0, unbekannt, journalGelesen: true };

    try {
        const { data, error } = await supabase.rpc('service_journal_void', {
            p_ids: ids,
            p_reason: reason,
        });
        if (error) {
            if (!isMissingRpc(error)) {
                console.warn('[serviceJournal] Ruecknahme fehlgeschlagen:', error);
            }
            return { voided: 0, unbekannt, journalGelesen: true, error };
        }
        // Die Zeile steht nicht mehr im Protokoll: bliebe ihre Art hinterlegt,
        // entschiede sie ueber einen JSON-Eintrag weiter, den ein spaeterer
        // Pfad unter derselben id neu geschrieben haben kann.
        vergissBuchungsartenFuer(vergessen);
        return { voided: typeof data === 'number' ? data : ids.length, unbekannt, journalGelesen: true };
    } catch (e) {
        console.warn('[serviceJournal] Ruecknahme fehlgeschlagen:', e);
        return { voided: 0, unbekannt, journalGelesen: true, error: e };
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// Das Ersetzen (SQL Nr. 136)
// ═══════════════════════════════════════════════════════════════════════════

/** Warum ein Ersetzen nicht gelungen ist. */
export type JournalErsetzenReason = 'leer' | 'rpc_fehlt' | 'fehler';

export interface JournalErsetzenResult {
    /**
     * Die RPC hat den Zielstand bestaetigt: die alten Zeilen sind
     * stillgelegt, die Ersatzzeilen leben. `false` mit Serverantwort heisst
     * NICHTS geschrieben — die Funktion laeuft in einer Transaktion und wirft
     * bei jeder Abweichung (ohne Antwort siehe `ersetzeJournalzeilen`).
     */
    ok: boolean;
    reason?: JournalErsetzenReason;
    /** Wiederholung: der Zielstand stand schon, der Aufruf hat nichts geschrieben. */
    unveraendert?: boolean;
    /** Die Journal-ids, die dieser Aufruf stillgelegt hat. */
    stillgelegt?: string[];
    /** Wie viele Ersatzzeilen dieser Aufruf eingefuegt hat. */
    eingefuegt?: number;
    error?: unknown;
}

/**
 * Lebende Journalzeilen ERSETZEN — stilllegen, dann einfuegen, in EINER
 * Transaktion (`service_journal_ersetzen`, SQL Nr. 136).
 *
 * ─── Warum nicht `mirrorEntriesToJournal…` plus `voidJournalEntries` ──────
 * Das Journal kennt kein Update; eine geaenderte Zeile wird durch eine neue
 * ersetzt. In ZWEI Aufrufen hat jede Reihenfolge einen Ausgang, der Geld
 * kostet: erst anhaengen verletzt bei einer Berichtszeile den
 * Eindeutigkeitsriegel `service_journal_bericht_leistung_uq` (23505 — die alte
 * Zeile lebt noch, das Journal behaelt die alte Menge; Befund 26051025), erst
 * stilllegen hinterlaesst bei einem Fehlschlag dazwischen KEINE lebende Zeile.
 * Nur serverseitig in einer Transaktion ist „stilllegen, dann einfuegen"
 * richtig.
 *
 * ─── Eingaben ─────────────────────────────────────────────────────────────
 * `alteZeilen` nennt jede zu ersetzende Zeile mit ihrer JOURNAL-id (aus
 * `ladeLebendeJournalzeilenZu`, keine JSON-id — nach einer frueheren
 * Teilruecknahme antwortet die JSON-id auf eine abgeleitete Zeile) und dem
 * ERWARTETEN Stand: der Menge, die sie tragen muss (Codex-Review R3). Die
 * Funktion prueft das unter der Zeilensperre und lehnt mit 55000 ab, wenn das
 * Journal sich seit dem Laden geaendert hat. `rows` sind fertige Zeilen aus
 * `toJournalRows`, dieselbe Form wie bei `appendToJournal`: die Funktion fuegt
 * ueber `service_journal_append` ein. Eine Ersatzzeile, die schon lebt, gehoert
 * NICHT in `alteZeilen` (die Funktion weist das als Widerspruch ab).
 *
 * ─── Ausfallrichtung ──────────────────────────────────────────────────────
 * Wirft nie. OHNE Ausgangskorb: die Antwort muss endgueltig sein — kaeme die
 * Ersetzung Stunden spaeter aus dem Korb nach, traefe sie einen Stand, den
 * niemand mehr geprueft hat. `rpc_fehlt` (Nr. 136 nicht gefahren) meldet der
 * Aufrufer selbst und nimmt seinen alten Weg; `fehler` mit Antwort des
 * Servers heisst: die Funktion hat abgelehnt, geschrieben ist nichts, die alte
 * Zeile lebt weiter (sichtbar in der Abweichungssicht). Riss die Verbindung
 * (keine Antwort), ist der Stand unbekannt — eine Wiederholung mit denselben
 * Daten ist dann trotzdem unschaedlich (die Funktion meldet `unveraendert`).
 */
export const ersetzeJournalzeilen = async (
    projectId: string,
    alteZeilen: ReadonlyArray<{ id: string | null | undefined; quantity: number }>,
    reason: JournalVoidReason,
    rows: ServiceJournalRow[],
): Promise<JournalErsetzenResult> => {
    const id = (projectId || '').trim();
    // Je Journal-id die ERSTE Nennung; leere ids fallen weg. Eine doppelte id
    // wiese die Funktion ab — hier genuegt eine.
    const alt: Array<{ id: string; quantity: number }> = [];
    for (const z of alteZeilen ?? []) {
        const zid = String(z?.id ?? '').trim();
        if (!zid || alt.some(a => a.id === zid)) continue;
        alt.push({ id: zid, quantity: Number(z?.quantity) });
    }
    if (!id || alt.length === 0 || !rows || rows.length === 0) return { ok: false, reason: 'leer' };

    try {
        const { data, error } = await supabase.rpc('service_journal_ersetzen', {
            p_project_id: id,
            p_old: alt,
            p_reason: reason,
            p_entries: rows,
        });
        if (error) {
            // Kein warn hier: der Aufrufer faellt auf seinen alten Weg zurueck
            // und sagt das selbst (und nur er weiss, was der Rueckfall ist).
            if (isMissingRpc(error)) return { ok: false, reason: 'rpc_fehlt', error };
            console.warn('[serviceJournal] Ersetzen fehlgeschlagen — nichts geschrieben:', error);
            void meldeSpiegelFehler(id, rows, error, 'Ersetzen');
            return { ok: false, reason: 'fehler', error };
        }

        // Ohne Fehler ist die Transaktion durch — die Funktion wirft bei
        // JEDER Abweichung. Die Felder werden deshalb nur ausgelesen, nicht
        // zur Bedingung gemacht: eine unerwartete Form hiesse nicht, dass
        // nichts geschrieben wurde.
        const antwort = (data && typeof data === 'object' ? data : {}) as {
            unveraendert?: unknown;
            stillgelegt?: unknown;
            eingefuegt?: unknown;
        };
        const stillgelegt = Array.isArray(antwort.stillgelegt) ? antwort.stillgelegt.map(String) : [];

        // Das Register wie beim Stilllegen und Anhaengen: die stillgelegten
        // Zeilen (und die JSON-id, auf die sie antworteten) vergessen, dann —
        // nur fuer ein geladenes Projekt — den Stand vom Server holen, der die
        // Ersatzzeile unter der JSON-id traegt.
        vergissBuchungsartenFuer([...alt.map(a => a.id), ...rows.map(r => r.legacy_entry_id)]);
        if (istProjektRegistriert(id)) {
            void ladeBuchungsartenInsRegister(id, { nurWennNochRegistriert: true }).catch(() => null);
        }
        return {
            ok: true,
            unveraendert: antwort.unveraendert === true,
            stillgelegt,
            eingefuegt: typeof antwort.eingefuegt === 'number' ? antwort.eingefuegt : undefined,
        };
    } catch (e) {
        // Ohne Antwort (Netz) ist NICHT sicher, dass nichts geschrieben wurde:
        // die Transaktion kann durch sein und nur die Antwort fehlen. Ein
        // spaeterer Aufruf mit denselben Daten ist trotzdem unschaedlich — die
        // Funktion erkennt den Zielstand und schreibt nichts (`unveraendert`).
        console.warn('[serviceJournal] Ersetzen ohne Antwort — ob der Server geschrieben hat, ist unbekannt:', e);
        void meldeSpiegelFehler(id, rows, e, 'Ersetzen');
        return { ok: false, reason: 'fehler', error: e };
    }
};
